import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  User,
  Calendar,
  Clock,
  FileText,
  Building,
  Users,
  AlertCircle,
  CheckCircle2,
  Mail,
  Phone,
  Search,
  Cake,
  Gift,
  Briefcase,
  TrendingUp,
  Tag,
  Shield,
  Compass,
  Sparkles,
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
  Download,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Filter,
  Award,
} from "lucide-react";

const ChatMessage = ({ message }) => {
  const isUser = message.sender === "user";
  let data = message.data || {};
  let displayType = message.display_type || "text";
  let messageText = message.text || "";
  let messageTitle = message.title || "";

  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'like' | 'dislike' | null
  const [teamSearch, setTeamSearch] = useState("");
  const [attRangeFilter, setAttRangeFilter] = useState("ALL");
  const [attRangeSearch, setAttRangeSearch] = useState("");
  const [isRangeTableOpen, setIsRangeTableOpen] = useState(true);
  const [attStatusFilter, setAttStatusFilter] = useState("ALL");
  const [attSearch, setAttSearch] = useState("");
  const [attPage, setAttPage] = useState(1);
  const [leaveBalSearch, setLeaveBalSearch] = useState("");
  const [leaveBalPage, setLeaveBalPage] = useState(1);
  const [monthlyAttFilter, setMonthlyAttFilter] = useState("ALL");
  const [monthlyAttSearch, setMonthlyAttSearch] = useState("");
  const [orgAttTab, setOrgAttTab] = useState("TRENDS"); // "TRENDS" | "ROSTER"
  const [orgAttSearch, setOrgAttSearch] = useState("");
  const [orgAttPage, setOrgAttPage] = useState(1);

  // AUTO-RECOVERY & PARSING: If the text is a raw Python dict or JSON
  if (!isUser && typeof messageText === "string" && (messageText.trim().startsWith("{") || messageText.trim().startsWith("["))) {
    try {
      const rawTrim = messageText.trim();
      let parsed = null;
      try {
        parsed = JSON.parse(rawTrim);
      } catch (e1) {
        // Python dict syntax: replace single quotes, None, True, False
        const sanitized = rawTrim
          .replace(/\bNone\b/g, "null")
          .replace(/\bTrue\b/g, "true")
          .replace(/\bFalse\b/g, "false")
          .replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, '"$1"');
        parsed = JSON.parse(sanitized);
      }

      if (parsed && typeof parsed === "object") {
        if (parsed.type === "monthly_daily_attendance") {
          displayType = "monthly_daily_attendance_card";
          data = parsed;
          messageTitle = `Daily Attendance (${parsed.month_name || ""} ${parsed.year || ""})`;
          messageText = `Verified month-wise daily attendance records for ${parsed.emp_name || "Employee"} (${parsed.emp_code || ""}).`;
        } else if (parsed.type === "organisation_monthly_attendance") {
          displayType = "table_organisation_monthly_attendance";
          data = parsed;
          messageTitle = parsed.category_title || `Organization Monthly Attendance (${parsed.month_name || ""} ${parsed.year || ""})`;
          messageText = `Organization-wide month-wise daily attendance and comprehensive employee roster across ${(parsed.employee_records && parsed.employee_records.length) || (parsed.summary && parsed.summary.total_employees) || 0} active employees.`;
        } else if (parsed.type === "attendance_range" || (parsed.records && parsed.summary)) {
          displayType = "attendance_range_card";
          data = parsed;
          messageTitle = `Attendance History (${parsed.start_date || ""} - ${parsed.end_date || ""})`;
          messageText = `Verified biometric attendance history for ${parsed.emp_name || "Employee"} (${parsed.emp_code || ""}) over ${parsed.total_days || 30} days.`;
        } else if (parsed.type === "team_members" || parsed.members) {
          displayType = "table_team_members";
          data = {
            ...parsed,
            members: (parsed.members || []).map((m) => ({
              ...m,
              name: m.name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.emp_code,
              designation: m.designation || m.designation__name || "-",
              department: m.department || m.department__name || "-",
              email: m.email || m.email_id || "-",
              phone: m.phone || m.phone_number || m.mobile_number || "-",
            })),
            total_count: parsed.total_count || parsed.count || (parsed.members ? parsed.members.length : 0),
          };
          messageTitle = data.category_title || "Team Directory";
          messageText = `Found **${data.total_count}** accessible team members under your organizational scope.`;
        } else if (parsed.type === "my_attendance" || parsed.work_hours || parsed.check_in) {
          displayType = "attendance_card";
          data = parsed;
          messageTitle = `Attendance Summary (${parsed.date || "Today"})`;
          messageText = `Verified biometric attendance log for ${parsed.emp_name || "Employee"} (${parsed.emp_code || ""}).`;
        } else if (parsed.type === "team_attendance") {
          displayType = "chart_donut";
          data = parsed;
          messageTitle = `Team Attendance (${parsed.date || "Today"})`;
          messageText = `Real-time attendance summary for all team members under your reporting scope.`;
        } else if (parsed.type === "leave_balance") {
          displayType = "metric_cards";
          data = parsed;
          messageTitle = `Available Leave Balances (${parsed.emp_code || ""})`;
          messageText = `Current verified active leave credit and comp-off balances.`;
        } else if (parsed.type === "organisation_leave_balances") {
          displayType = "table_leave_balances";
          data = parsed;
          messageTitle = parsed.category_title || `Organization Leave Balances (${parsed.year || ""})`;
          messageText = `Verified leave credit ledger across ${parsed.total_employees || (parsed.records ? parsed.records.length : 0)} active employees.`;
        } else if (parsed.type === "team_leaves" || parsed.leaves) {
          displayType = "table_leaves";
          data = parsed;
          messageTitle = "Team Leave Requests";
          messageText = `Found **${parsed.count || (parsed.leaves && parsed.leaves.length) || 0}** leave applications.`;
        } else if (parsed.type === "helpdesk_tickets" || parsed.tickets) {
          displayType = "table_tickets";
          data = parsed;
          messageTitle = "Helpdesk Support Tickets";
          messageText = `Found **${parsed.count || 0}** support tickets assigned.`;
        } else if (parsed.type === "payslip") {
          displayType = "payslip_card";
          data = parsed;
          messageTitle = `Payslip Summary for ${parsed.period || ""}`;
          messageText = `Confidential compensation summary and net payable details.`;
        } else if (parsed.type === "leave_application_result") {
          displayType = "leave_application_card";
          data = parsed;
          messageTitle = "Leave Application Submitted";
          messageText = `Your leave request has been processed and routed to your reporting hierarchy.`;
        } else if (parsed.type === "permission_application_result") {
          displayType = "permission_application_card";
          data = parsed;
          messageTitle = "Permission Request Submitted";
          messageText = `Your permission request has been processed and recorded with quota validation.`;
        } else if (parsed.type === "department_hod") {
          displayType = "profile_card";
          data = {
            name: parsed.hod_name,
            emp_code: parsed.emp_code,
            designation: parsed.designation,
            department: parsed.department,
            work_location: parsed.location,
            reporting_manager: parsed.reporting_to,
            status: "Active HOD",
          };
          messageTitle = `Head of Department – ${parsed.department}`;
        }
      }
    } catch (err) {
      // Keep original text if unparseable
    }
  }

  // Download Attendance Range as Excel
  const handleDownloadAttendanceRangeExcel = (rangeData) => {
    try {
      const empName = rangeData.emp_name || "Employee";
      const empCode = rangeData.emp_code || "EMP";
      const startDate = rangeData.start_date || "";
      const endDate = rangeData.end_date || "";
      const summary = rangeData.summary || {};
      const records = rangeData.records || [];

      const rows = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS HR OPERATIONS"],
        ["EMPLOYEE BIOMETRIC ATTENDANCE REPORT"],
        ["Generated from BEMSS AI Assistant", "", "", "", `Generated On: ${new Date().toLocaleString()}`],
        [],
        ["1. EMPLOYEE & REPORT DETAILS"],
        ["Employee Name", empName, "Employee Code", empCode],
        ["Department", rangeData.department || "-", "Designation", rangeData.designation || "-"],
        ["Date Range", `${startDate} to ${endDate}`, "Total Calendar Days", rangeData.total_days || records.length],
        [],
        ["2. ATTENDANCE METRICS SUMMARY"],
        ["Metric", "Value", "Metric", "Value"],
        ["Total Calendar Days", summary.total_days || records.length, "Working Days", summary.working_days || "-"],
        ["Present Days", summary.present_days || 0, "Attendance Percentage", `${summary.attendance_rate || 0}%`],
        ["Absent / Unmarked Days", summary.absent_days || 0, "Late Arrivals", summary.late_days || 0],
        ["Half Days", summary.half_days || 0, "Leaves Taken", summary.leave_days || 0],
        ["Sundays & Holidays", summary.sundays_holidays || 0, "Total Work Time", summary.total_work_time || "-"],
        ["Average Daily Hours", summary.avg_daily_hours || "-", "Biometric Source", "COSEC & Biomax Central Synced"],
        [],
        ["3. DAY-BY-DAY ATTENDANCE LOG"],
        ["Sl No", "Date", "Day", "Status", "Check-In", "Check-Out", "Work Hours", "Late Status", "Remarks"]
      ];

      records.forEach((rec, idx) => {
        rows.push([
          idx + 1,
          rec.date || rec.raw_date || "-",
          rec.day || "-",
          rec.status || "-",
          rec.check_in || "-",
          rec.check_out || "-",
          rec.work_hours || "-",
          rec.is_late ? "Yes (Late)" : "No",
          rec.remarks || "-"
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(rows);

      ws["!cols"] = [
        { wch: 8 },   // Sl No
        { wch: 16 },  // Date
        { wch: 8 },   // Day
        { wch: 22 },  // Status
        { wch: 14 },  // Check-In
        { wch: 14 },  // Check-Out
        { wch: 14 },  // Work Hours
        { wch: 14 },  // Late
        { wch: 32 },  // Remarks
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance_Log");

      const cleanCode = empCode.replace(/[^a-zA-Z0-9]/g, "_");
      const cleanStart = startDate.replace(/[^a-zA-Z0-9]/g, "_");
      const cleanEnd = endDate.replace(/[^a-zA-Z0-9]/g, "_");
      const filename = `Attendance_${cleanCode}_${cleanStart}_to_${cleanEnd}.xlsx`;

      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error("Failed to generate Excel download:", err);
    }
  };

  // Download Single Day Attendance as Excel
  const handleDownloadSingleAttendanceExcel = (attData) => {
    try {
      const rows = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS HR OPERATIONS"],
        ["DAILY ATTENDANCE RECORD"],
        ["Generated from BEMSS AI Assistant", `Export Date: ${new Date().toLocaleString()}`],
        [],
        ["Field", "Value"],
        ["Employee Name", attData.emp_name || "-"],
        ["Employee Code", attData.emp_code || "-"],
        ["Attendance Date", attData.date || "-"],
        ["Status", attData.status || "-"],
        ["First Check-In", attData.check_in || "-"],
        ["Last Check-Out", attData.check_out || "-"],
        ["Total Work Hours", attData.work_hours || "00:00"],
        ["Late Arrival", attData.is_late ? "Yes (Late)" : "No"]
      ];
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [{ wch: 22 }, { wch: 35 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Daily_Attendance");
      const cleanCode = (attData.emp_code || "EMP").replace(/[^a-zA-Z0-9]/g, "_");
      const cleanDate = (attData.date || "Today").replace(/[^a-zA-Z0-9]/g, "_");
      XLSX.writeFile(wb, `Attendance_${cleanCode}_${cleanDate}.xlsx`);
    } catch (err) {
      console.error("Failed to export daily attendance to Excel:", err);
    }
  };

  // Download Team Attendance as Excel
  const handleDownloadTeamAttendanceExcel = (teamAttData) => {
    try {
      const records = teamAttData.records || [];
      const rows = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS HR OPERATIONS"],
        [`TEAM ATTENDANCE REPORT (${teamAttData.date || "Today"})`],
        [`Total Members: ${teamAttData.total_team_size || records.length}`, `Present: ${teamAttData.present || 0}`, `Absent: ${teamAttData.absent || 0}`, `Late: ${teamAttData.late || 0}`, `Half Day: ${teamAttData.half_day || 0}`],
        ["Generated from BEMSS AI Assistant", `Export Date: ${new Date().toLocaleString()}`],
        [],
        ["S.No", "Emp Code", "Employee Name", "Designation", "Department", "Status", "Check-In", "Check-Out", "Work Hours", "Remarks"]
      ];

      records.forEach((r, idx) => {
        rows.push([
          idx + 1,
          r.emp_code || "-",
          r.emp_name || "-",
          r.designation || "-",
          r.department || "-",
          r.status || "-",
          r.check_in || "-",
          r.check_out || "-",
          r.work_hours || "00:00",
          r.remarks || "-"
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [
        { wch: 6 },
        { wch: 12 },
        { wch: 24 },
        { wch: 22 },
        { wch: 18 },
        { wch: 14 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 25 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Team_Attendance");
      const cleanDate = (teamAttData.date || "Today").replace(/[^a-zA-Z0-9]/g, "_");
      XLSX.writeFile(wb, `Team_Attendance_${cleanDate}.xlsx`);
    } catch (err) {
      console.error("Failed to export team attendance to Excel:", err);
    }
  };

  // Download Organisation Leave Balances as Excel (Only CL, SL, Off Days)
  const handleDownloadOrganisationLeaveBalancesExcel = (lbData) => {
    try {
      const records = lbData.records || [];
      const rows = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS HR OPERATIONS"],
        [`ORGANISATION LEAVE BALANCES REPORT (${lbData.year || new Date().getFullYear()})`],
        [
          `Total Employees: ${lbData.total_employees || records.length}`,
          `Total Leave Pool: ${lbData.total_leave_pool || 0} days`,
          `Avg Available: ${lbData.avg_available_balance || 0} days`
        ],
        [
          `Total Casual Leave (CL): ${lbData.total_cl || 0}`,
          `Total Sick Leave (SL): ${lbData.total_sl || 0}`,
          `Total Off Days (Comp-Off): ${lbData.total_off_days ?? lbData.total_compoff ?? 0}`
        ],
        ["Generated from BEMSS AI Assistant", `Export Date: ${new Date().toLocaleString()}`],
        [],
        ["S.No", "Emp Code", "Employee Name", "Department", "Designation", "Casual Leave (CL)", "Sick Leave (SL)", "Off Days (Comp-Off)", "Total Available (Days)"]
      ];

      records.forEach((r, idx) => {
        rows.push([
          idx + 1,
          r.emp_code || "-",
          r.emp_name || "-",
          r.department || "-",
          r.designation || "-",
          r.casual_leave ?? 0,
          r.sick_leave ?? 0,
          r.off_days ?? r.compoff ?? 0,
          r.total_available ?? 0
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [
        { wch: 6 },
        { wch: 14 },
        { wch: 26 },
        { wch: 20 },
        { wch: 22 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 22 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Leave_Balances");
      XLSX.writeFile(wb, `Leave_Balances_${lbData.year || new Date().getFullYear()}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error("Failed to export leave balances to Excel:", err);
    }
  };

  // Download Personal Month-Wise Daily Attendance as Excel
  const handleDownloadMyMonthlyDailyAttendanceExcel = (mAttData) => {
    try {
      const records = mAttData.records || [];
      const summary = mAttData.summary || {};
      const rows = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS HR OPERATIONS"],
        [`MONTHLY DAILY ATTENDANCE REPORT — ${mAttData.month_name || ""} ${mAttData.year || ""}`],
        ["Employee", `${mAttData.emp_name || "Employee"} (${mAttData.emp_code || "-"})`, "Department", mAttData.department || "-", "Designation", mAttData.designation || "-"],
        [
          `Working Days: ${summary.working_days || 0}`,
          `Present Days: ${summary.present_days || 0}`,
          `Absent Days: ${summary.absent_days || 0}`,
          `Late Days: ${summary.late_days || 0}`,
          `Half Days: ${summary.half_days || 0}`,
          `Off Days: ${summary.off_days || 0}`,
          `Attendance Rate: ${summary.attendance_rate || 0}%`
        ],
        ["Generated from BEMSS AI Assistant", `Export Date: ${new Date().toLocaleString()}`],
        [],
        ["S.No", "Date", "Day", "Status", "Check-In", "Check-Out", "Work Hours", "Late Status", "Remarks"]
      ];

      records.forEach((r, idx) => {
        rows.push([
          idx + 1,
          r.formatted_date || r.date || "-",
          r.day || "-",
          r.status || "-",
          r.check_in || "-",
          r.check_out || "-",
          r.work_hours || "00:00",
          r.is_late ? "Late" : "On-Time",
          r.remarks || "-"
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [
        { wch: 6 },
        { wch: 14 },
        { wch: 12 },
        { wch: 22 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 14 },
        { wch: 28 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Monthly_Daily_Attendance");
      const cleanCode = (mAttData.emp_code || "EMP").replace(/[^a-zA-Z0-9]/g, "_");
      const cleanMonth = (mAttData.month_name || "Month").replace(/[^a-zA-Z0-9]/g, "_");
      XLSX.writeFile(wb, `Daily_Attendance_${cleanCode}_${cleanMonth}_${mAttData.year || ""}.xlsx`);
    } catch (err) {
      console.error("Failed to export monthly daily attendance to Excel:", err);
    }
  };

  // Download Organisation Month-Wise Daily Attendance as Excel (Dual Sheets)
  const handleDownloadOrganisationMonthlyAttendanceExcel = (orgData) => {
    try {
      const summary = orgData.summary || {};
      const dailyTrends = orgData.daily_trends || [];
      const empRecords = orgData.employee_records || [];

      // Sheet 1: Daily Trends
      const trendRows = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS HR OPERATIONS"],
        [`ORGANISATION MONTH-WISE DAILY ATTENDANCE TRENDS — ${orgData.month_name || ""} ${orgData.year || ""}`],
        [
          `Total Employees: ${summary.total_employees || empRecords.length}`,
          `Working Days: ${summary.working_days || 0}`,
          `Off Days: ${summary.off_days || 0}`,
          `Avg Attendance Rate: ${summary.avg_attendance_rate || 0}%`,
          `Present Man-Days: ${summary.total_present_man_days || 0}`,
          `Absent Man-Days: ${summary.total_absent_man_days || 0}`
        ],
        ["Generated from BEMSS AI Assistant", `Export Date: ${new Date().toLocaleString()}`],
        [],
        ["S.No", "Date", "Day", "Off Day / Working", "Present Count", "Absent Count", "Late Count", "Attendance %", "Remarks"]
      ];

      dailyTrends.forEach((t, idx) => {
        trendRows.push([
          idx + 1,
          t.date || "-",
          t.day || "-",
          t.is_off_day ? "Off Day" : "Working Day",
          t.present || 0,
          t.absent || 0,
          t.late || 0,
          `${t.attendance_rate || 0}%`,
          t.off_reason || "-"
        ]);
      });

      // Sheet 2: Employee Monthly Roster
      const empRows = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS HR OPERATIONS"],
        [`ALL EMPLOYEES MONTHLY ATTENDANCE SUMMARY — ${orgData.month_name || ""} ${orgData.year || ""}`],
        [`Total Employees: ${empRecords.length}`, `Month: ${orgData.month_name} ${orgData.year}`],
        ["Generated from BEMSS AI Assistant", `Export Date: ${new Date().toLocaleString()}`],
        [],
        ["S.No", "Emp Code", "Employee Name", "Department", "Designation", "Present Days", "Absent Days", "Late Days", "Half Days", "Off Days", "Working Days", "Attendance Rate %"]
      ];

      empRecords.forEach((e, idx) => {
        empRows.push([
          idx + 1,
          e.emp_code || "-",
          e.emp_name || "-",
          e.department || "-",
          e.designation || "-",
          e.present_days || 0,
          e.absent_days || 0,
          e.late_days || 0,
          e.half_days || 0,
          e.off_days || 0,
          e.working_days || 0,
          `${e.attendance_rate || 0}%`
        ]);
      });

      const wb = XLSX.utils.book_new();

      const wsTrends = XLSX.utils.aoa_to_sheet(trendRows);
      wsTrends["!cols"] = [
        { wch: 6 }, { wch: 14 }, { wch: 12 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 25 }
      ];
      XLSX.utils.book_append_sheet(wb, wsTrends, "Daily_Attendance_Trends");

      const wsEmps = XLSX.utils.aoa_to_sheet(empRows);
      wsEmps["!cols"] = [
        { wch: 6 }, { wch: 14 }, { wch: 26 }, { wch: 20 }, { wch: 22 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 18 }
      ];
      XLSX.utils.book_append_sheet(wb, wsEmps, "All_Employees_Roster");

      const cleanMonth = (orgData.month_name || "Month").replace(/[^a-zA-Z0-9]/g, "_");
      XLSX.writeFile(wb, `Organisation_Monthly_Attendance_${cleanMonth}_${orgData.year || ""}.xlsx`);
    } catch (err) {
      console.error("Failed to export organisation monthly attendance to Excel:", err);
    }
  };

  // Download Team Members as Excel
  const handleDownloadTeamExcel = (teamData) => {
    try {
      const rows = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS TEAM DIRECTORY"],
        [`Category: ${teamData.category_title || "Team Members"}`, `Total Count: ${teamData.total_count || (teamData.members ? teamData.members.length : 0)}`],
        [`Generated On: ${new Date().toLocaleString()}`],
        [],
        ["Sl No", "Employee Code", "Full Name", "Designation", "Department", "Email", "Phone", "Reporting To"]
      ];
      (teamData.members || []).forEach((m, idx) => {
        rows.push([
          idx + 1,
          m.emp_code || "-",
          m.name || "-",
          m.designation || "-",
          m.department || "-",
          m.email || "-",
          m.phone || "-",
          m.reporting_to || "-"
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [
        { wch: 8 }, { wch: 14 }, { wch: 24 }, { wch: 22 }, { wch: 20 }, { wch: 25 }, { wch: 16 }, { wch: 20 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Team_Directory");
      XLSX.writeFile(wb, `Team_Directory_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error("Failed to export team directory to Excel:", err);
    }
  };

  // Download Comp-Off Statement as Excel
  const handleDownloadCompoffExcel = (compoffData) => {
    try {
      const monthLabel = `${compoffData.month_name || ""} ${compoffData.year || ""}`.trim();
      const rows = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS COMP-OFF STATEMENT"],
        [`Employee: ${compoffData.emp_name || "-"} (${compoffData.emp_code || "-"})`, `Period: ${monthLabel}`],
        [`Available Balance: ${compoffData.available_balance || 0} Days`, `Taken in Period: ${compoffData.taken_days || 0} Days`, `Earned in Period: ${compoffData.credits_earned_this_month || 0} Days`],
        [`Generated On: ${new Date().toLocaleString()}`],
        [],
        ["--- SECTION 1: COMP-OFF LEAVES TAKEN ---"],
        ["Sl No", "Leave Date", "Days", "Duration", "Status", "Reason"]
      ];

      if (compoffData.taken_records && compoffData.taken_records.length > 0) {
        compoffData.taken_records.forEach((r, idx) => {
          rows.push([
            idx + 1,
            r.date || "-",
            r.days || 1.0,
            r.duration_type || "Full Day",
            r.status || "Approved",
            r.reason || "-"
          ]);
        });
      } else {
        rows.push(["-", "No comp-off leaves taken in this period", "-", "-", "-", "-"]);
      }

      rows.push([]);
      rows.push(["--- SECTION 2: ACTIVE COMP-OFF CREDITS ---"]);
      rows.push(["Sl No", "Work Date (Sunday/Holiday)", "Credit Balance (Days)", "Status", "Expiry Date"]);

      if (compoffData.valid_credits && compoffData.valid_credits.length > 0) {
        compoffData.valid_credits.forEach((c, idx) => {
          rows.push([
            idx + 1,
            c.work_date || "-",
            c.days_available || 1.0,
            c.status || "Available",
            c.expiry_date || "-"
          ]);
        });
      } else {
        rows.push(["-", "No active credits found", "-", "-", "-"]);
      }

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [
        { wch: 8 }, { wch: 22 }, { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 35 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Compoff_Statement");
      const cleanCode = (compoffData.emp_code || "EMP").replace(/[^a-zA-Z0-9]/g, "_");
      XLSX.writeFile(wb, `Compoff_Statement_${cleanCode}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error("Failed to export comp-off statement to Excel:", err);
    }
  };

  // Handle Copy to clipboard
  const handleCopy = () => {
    const textToCopy = `${messageTitle ? messageTitle + "\n\n" : ""}${messageText}`;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Download Any Markdown Table as Excel
  const handleDownloadMarkdownTableExcel = (headers, rows, title) => {
    try {
      const cleanTitle = (title || "Table_Data").replace(/[^a-zA-Z0-9_\s-]/g, "").trim() || "Table_Export";
      const cleanCell = (val) => {
        if (val == null) return "";
        return String(val)
          .replace(/<br\s*\/?>/gi, " ")
          .replace(/&nbsp;/gi, " ")
          .replace(/<\/?(?:span|div|p|b|i|strong|em|font)[^>]*>/gi, "")
          .replace(/\*\*(.*?)\*\*/g, "$1")
          .replace(/`([^`]+)`/g, "$1")
          .replace(/\*(.*?)\*/g, "$1")
          .trim();
      };
      const cleanedHeaders = (headers || []).map(cleanCell);
      const cleanedRows = (rows || []).map((row) => (row || []).map(cleanCell));

      const sheetData = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS SYSTEM EXPORT"],
        [`Table: ${cleanTitle}`, `Exported On: ${new Date().toLocaleString()}`],
        [],
        cleanedHeaders,
        ...cleanedRows,
      ];
      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      ws["!cols"] = cleanedHeaders.map((h, i) => {
        let maxLen = (h || "").length;
        cleanedRows.forEach((r) => {
          if (r[i]) maxLen = Math.max(maxLen, String(r[i]).length);
        });
        return { wch: Math.min(45, Math.max(12, maxLen + 3)) };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, cleanTitle.slice(0, 31));
      const filename = `${cleanTitle.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error("Failed to export markdown table to Excel:", err);
    }
  };

  // Download Helpdesk Tickets as Excel
  const handleDownloadTicketsExcel = (tickets) => {
    try {
      const sheetData = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS HELPDESK TICKETS"],
        [`Exported On: ${new Date().toLocaleString()}`, `Total Tickets: ${tickets.length}`],
        [],
        ["Ticket #", "Subject", "Priority", "Status", "Created At"]
      ];
      tickets.forEach((t) => {
        sheetData.push([
          `#${t.ticket_number || t.id || "-"}`,
          t.subject || "-",
          t.priority || "-",
          t.status || "-",
          t.created_at || "-"
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      ws["!cols"] = [{ wch: 14 }, { wch: 35 }, { wch: 14 }, { wch: 16 }, { wch: 18 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Helpdesk_Tickets");
      XLSX.writeFile(wb, `Helpdesk_Tickets_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error("Failed to export tickets to Excel:", err);
    }
  };

  // Download Leave Requests as Excel
  const handleDownloadLeavesExcel = (leaves) => {
    try {
      const sheetData = [
        ["BAASHYAAM CONSTRUCTIONS - BEMSS LEAVE REQUESTS"],
        [`Exported On: ${new Date().toLocaleString()}`, `Total Requests: ${leaves.length}`],
        [],
        ["Leave Type", "From Date", "To Date", "Total Days", "Status", "Reason"]
      ];
      leaves.forEach((l) => {
        sheetData.push([
          l.leave_type || "-",
          l.from_date || "-",
          l.to_date || "-",
          l.total_days || "-",
          l.leave_status || l.status || "-",
          l.reason || "-"
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      ws["!cols"] = [{ wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 30 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Leave_Requests");
      XLSX.writeFile(wb, `Leave_Requests_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error("Failed to export leaves to Excel:", err);
    }
  };

  // Inline formatting helper for bold, code, italics, links, and badges
  const renderInlineMarkdown = (text) => {
    if (!text) return null;
    let str = String(text);

    // Strip HTML tags and entities so they never appear as raw text in chat
    str = str
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/<\/?(?:span|div|p|b|i|strong|em|font|table|thead|tbody|tr|td|th)[^>]*>/gi, "");

    const parts = str.split(/(\*\*.*?\*\*|`.*?`|\*.*?\*|\[.*?\]\(.*?\)|<https?:\/\/.*?>|https?:\/\/[^\s<)]+)/g);
    return parts.map((part, pIdx) => {
      if (!part) return null;
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={pIdx} style={{ color: isUser ? "#ffffff" : "#0e497b", fontWeight: "600" }}>
            {part.slice(2, -2)}
          </strong>
        );
      }
      if (part.startsWith("`") && part.endsWith("`")) {
        return (
          <code
            key={pIdx}
            style={{
              background: isUser ? "rgba(255,255,255,0.2)" : "rgba(14, 73, 123, 0.08)",
              color: isUser ? "#ffffff" : "#0e497b",
              border: isUser ? "none" : "1px solid rgba(14, 73, 123, 0.2)",
              padding: "1px 5px",
              borderRadius: "4px",
              fontSize: "11.5px",
              fontFamily: "monospace",
            }}
          >
            {part.slice(1, -1)}
          </code>
        );
      }
      if (part.startsWith("*") && part.endsWith("*") && !part.startsWith("**")) {
        return (
          <em key={pIdx} style={{ color: isUser ? "#e0f2fe" : "#64748b" }}>
            {part.slice(1, -1)}
          </em>
        );
      }
      const mdLinkMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
      if (mdLinkMatch) {
        return (
          <a
            key={pIdx}
            href={mdLinkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: isUser ? "#93c5fd" : "#1d4ed8", textDecoration: "underline", fontWeight: "600" }}
          >
            {mdLinkMatch[1]}
          </a>
        );
      }
      if (part.startsWith("<http") && part.endsWith(">")) {
        const url = part.slice(1, -1);
        return (
          <a
            key={pIdx}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: isUser ? "#93c5fd" : "#1d4ed8", textDecoration: "underline", fontWeight: "600" }}
          >
            {url}
          </a>
        );
      }
      if (part.startsWith("http://") || part.startsWith("https://")) {
        return (
          <a
            key={pIdx}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: isUser ? "#93c5fd" : "#1d4ed8", textDecoration: "underline", fontWeight: "600" }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  // Helper to parse markdown blocks including tables, headings, callouts, and lists
  const parseMarkdownBlocks = (rawText) => {
    if (!rawText) return [];

    // Normalize and clean HTML tags and entities
    const cleanRaw = String(rawText)
      .replace(/&nbsp;/gi, " ")
      .replace(/<\/?(?:span|div|p|b|i|strong|em|font)[^>]*>/gi, "");

    const rawLines = cleanRaw.split("\n");
    const lines = [];
    for (const l of rawLines) {
      const trimmed = l.trim();
      if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
        // Table row: replace <br> with ' · ' to keep table row syntax intact
        lines.push(l.replace(/<br\s*\/?>/gi, " · "));
      } else {
        // Non-table line: replace <br> with newline so it breaks naturally
        const sub = l.replace(/<br\s*\/?>/gi, "\n").split("\n");
        lines.push(...sub);
      }
    }

    const blocks = [];
    let currentTable = null;
    let textBuffer = [];
    let lastHeading = "";

    const flushText = () => {
      if (textBuffer.length > 0) {
        blocks.push({ type: "text", lines: [...textBuffer] });
        textBuffer = [];
      }
    };

    const isTableRow = (line) => {
      const trimmed = line.trim();
      return trimmed.startsWith("|") && trimmed.endsWith("|") && trimmed.split("|").length >= 3;
    };

    const isSeparatorRow = (line) => {
      const trimmed = line.trim();
      return isTableRow(line) && trimmed.replace(/[|\s\-:—]/g, "").length === 0;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      if (trimmed.startsWith("###") || trimmed.startsWith("##") || trimmed.startsWith("#")) {
        lastHeading = trimmed.replace(/^#+\s*/, "").replace(/[^\w\s-]/g, "").trim();
      }

      if (isTableRow(line)) {
        if (!currentTable) {
          flushText();
          currentTable = {
            title: lastHeading || "Data Table",
            headers: line
              .split("|")
              .slice(1, -1)
              .map((c) => c.trim()),
            rows: [],
          };
        } else {
          if (isSeparatorRow(line)) {
            continue;
          }
          const cells = line
            .split("|")
            .slice(1, -1)
            .map((c) => c.trim());
          if (cells.length > 0) {
            currentTable.rows.push(cells);
          }
        }
      } else {
        if (currentTable) {
          blocks.push({ type: "table", ...currentTable });
          currentTable = null;
        }
        textBuffer.push(line);
      }
    }

    if (currentTable) {
      blocks.push({ type: "table", ...currentTable });
    }
    flushText();
    return blocks;
  };

  // Comprehensive Markdown & Table Renderer with 1-Click Excel Download
  const renderFormattedText = (rawText) => {
    if (!rawText) return null;
    const blocks = parseMarkdownBlocks(rawText);

    return blocks.map((block, bIdx) => {
      if (block.type === "table") {
        return (
          <div
            key={bIdx}
            style={{
              margin: "12px 0",
              borderRadius: "12px",
              background: "#ffffff",
              border: "1px solid rgba(14, 73, 123, 0.22)",
              boxShadow: "0 3px 12px rgba(14, 73, 123, 0.08)",
              overflow: "hidden",
            }}
          >
            {/* Table Header Bar with Title and Excel Download Button */}
            <div
              style={{
                padding: "8px 12px",
                background: "linear-gradient(135deg, rgba(240, 247, 255, 0.95), rgba(224, 242, 254, 0.75))",
                borderBottom: "1px solid rgba(14, 73, 123, 0.18)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <FileSpreadsheet size={15} color="#0e497b" />
                <span style={{ fontSize: "12.5px", fontWeight: "700", color: "#0e497b" }}>
                  {block.title || "Table"}
                </span>
                <span
                  style={{
                    fontSize: "10.5px",
                    background: "rgba(14, 73, 123, 0.1)",
                    color: "#0e497b",
                    padding: "1px 7px",
                    borderRadius: "10px",
                    fontWeight: "700",
                  }}
                >
                  {block.rows.length} rows
                </span>
              </div>

              {/* 1-Click Excel Download Button */}
              <button
                onClick={() => handleDownloadMarkdownTableExcel(block.headers, block.rows, block.title)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  background: "linear-gradient(135deg, #059669, #10b981)",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "5px 11px",
                  fontSize: "11px",
                  fontWeight: "700",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(16, 185, 129, 0.25)",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-1px)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                title="Download this table as Excel (.xlsx)"
              >
                <FileSpreadsheet size={13} />
                <span>Download Excel (.xlsx)</span>
                <Download size={11} />
              </button>
            </div>

            {/* Scrollable Table View */}
            <div style={{ overflowX: "auto", maxHeight: "360px", overflowY: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                  textAlign: "left",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "rgba(240, 247, 255, 0.95)",
                      borderBottom: "2px solid rgba(14, 73, 123, 0.2)",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {block.headers.map((h, hIdx) => (
                      <th
                        key={hIdx}
                        style={{
                          padding: "8px 12px",
                          color: "#0e497b",
                          fontWeight: "700",
                          whiteSpace: "nowrap",
                          fontSize: "11.5px",
                        }}
                      >
                        {renderInlineMarkdown(h)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rIdx) => (
                    <tr
                      key={rIdx}
                      style={{
                        borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                        background: rIdx % 2 === 0 ? "#ffffff" : "rgba(248, 250, 252, 0.65)",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(240, 247, 255, 0.7)")}
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = rIdx % 2 === 0 ? "#ffffff" : "rgba(248, 250, 252, 0.65)")
                      }
                    >
                      {row.map((cell, cIdx) => (
                        <td
                          key={cIdx}
                          style={{
                            padding: "7px 12px",
                            color: "#1e293b",
                            verticalAlign: "top",
                            lineHeight: "1.45",
                          }}
                        >
                          {renderInlineMarkdown(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      // Non-table lines
      return (
        <div key={bIdx} style={{ marginBottom: "6px" }}>
          {block.lines.map((line, lIdx) => {
            const trimmed = line.trim();

            if (!trimmed) {
              return <div key={lIdx} style={{ height: "6px" }} />;
            }

            if (trimmed === "---" || trimmed === "***" || trimmed === "___") {
              return (
                <hr
                  key={lIdx}
                  style={{
                    border: "none",
                    borderTop: "1px solid rgba(14, 73, 123, 0.15)",
                    margin: "10px 0",
                  }}
                />
              );
            }

            if (trimmed.startsWith("###")) {
              return (
                <div
                  key={lIdx}
                  style={{
                    fontSize: "13.5px",
                    fontWeight: "700",
                    color: isUser ? "#ffffff" : "#0e497b",
                    margin: "10px 0 4px 0",
                  }}
                >
                  {renderInlineMarkdown(trimmed.replace(/^#+\s*/, ""))}
                </div>
              );
            }

            if (trimmed.startsWith("##") || trimmed.startsWith("#")) {
              return (
                <div
                  key={lIdx}
                  style={{
                    fontSize: "14.5px",
                    fontWeight: "800",
                    color: isUser ? "#ffffff" : "#0e497b",
                    margin: "12px 0 6px 0",
                  }}
                >
                  {renderInlineMarkdown(trimmed.replace(/^#+\s*/, ""))}
                </div>
              );
            }

            if (trimmed.startsWith(">")) {
              return (
                <div
                  key={lIdx}
                  style={{
                    margin: "8px 0",
                    padding: "7px 12px",
                    background: isUser ? "rgba(255, 255, 255, 0.15)" : "rgba(14, 73, 123, 0.06)",
                    borderLeft: isUser ? "3px solid #ffffff" : "3px solid #0e497b",
                    borderRadius: "4px",
                    fontSize: "12px",
                    color: isUser ? "#f1f5f9" : "#334155",
                  }}
                >
                  {renderInlineMarkdown(trimmed.replace(/^>\s*/, ""))}
                </div>
              );
            }

            return (
              <div key={lIdx} style={{ minHeight: "18px", marginBottom: "2px" }}>
                {renderInlineMarkdown(line)}
              </div>
            );
          })}
        </div>
      );
    });
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom: "20px",
        gap: "12px",
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      {/* ── AI AVATAR ON THE LEFT (BLUE THEME) ── */}
      {!isUser && (
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "#0e497b",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#38bdf8",
            flexShrink: 0,
            boxShadow: "0 4px 10px rgba(14, 73, 123, 0.25)",
            marginTop: "2px",
          }}
        >
          <Sparkles size={18} color="#38bdf8" />
        </div>
      )}

      {/* ── MESSAGE BUBBLE / CARD ── */}
      <div
        style={{
          maxWidth: isUser ? "80%" : "88%",
          minWidth: isUser ? "auto" : "320px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: isUser ? "12px 18px" : "16px 20px",
            borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
            background: isUser
              ? "linear-gradient(135deg, #0e497b 0%, #1d4ed8 100%)"
              : "rgba(255, 255, 255, 0.92)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            color: isUser ? "#ffffff" : "#1e293b",
            border: isUser ? "none" : "1px solid rgba(14, 73, 123, 0.18)",
            boxShadow: isUser
              ? "0 4px 14px rgba(14, 73, 123, 0.28)"
              : "0 4px 20px -2px rgba(14, 73, 123, 0.08), 0 2px 6px -1px rgba(0, 0, 0, 0.03)",
            wordBreak: "break-word",
            fontSize: "13.5px",
            lineHeight: "1.6",
            position: "relative",
          }}
        >
          {/* Card Title in Deep Corporate Blue */}
          {messageTitle && !isUser && (
            <div
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "16px",
                fontWeight: "700",
                color: "#0e497b",
                marginBottom: "6px",
                letterSpacing: "0.2px",
              }}
            >
              {messageTitle}
            </div>
          )}

          {/* Formatted Text Content */}
          <div style={{ color: isUser ? "#ffffff" : "#1e293b" }}>
            {renderFormattedText(messageText)}
          </div>

          {/* ══════════════════════════════════════════════════ */}
          {/* 1. WIDGET: Team Members Directory                   */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "table_team_members" && data?.members && (
            <div
              style={{
                marginTop: "14px",
                padding: "14px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
                boxShadow: "0 4px 14px rgba(14, 73, 123, 0.06)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Users size={16} color="#0e497b" />
                  <span style={{ fontSize: "13px", fontWeight: "700", color: "#0e497b" }}>
                    {data.category_title || "Team Directory"}
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      background: "rgba(14, 73, 123, 0.1)",
                      color: "#0e497b",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontWeight: "700",
                    }}
                  >
                    {data.total_count || data.members.length} Members
                  </span>
                </div>

                {/* Controls: Search filter & Excel Download */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "#ffffff",
                      borderRadius: "20px",
                      padding: "3px 10px",
                      border: "1px solid #0e497b",
                    }}
                  >
                    <Search size={13} color="#64748b" style={{ marginRight: "6px" }} />
                    <input
                      type="text"
                      placeholder="Filter name or code..."
                      value={teamSearch}
                      onChange={(e) => setTeamSearch(e.target.value)}
                      style={{
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        color: "#0f172a",
                        fontSize: "12px",
                        width: "130px",
                      }}
                    />
                  </div>

                  <button
                    onClick={() => handleDownloadTeamExcel(data)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      background: "rgba(16, 185, 129, 0.12)",
                      color: "#059669",
                      border: "1px solid rgba(16, 185, 129, 0.3)",
                      borderRadius: "16px",
                      padding: "4px 10px",
                      fontSize: "11px",
                      fontWeight: "700",
                      cursor: "pointer",
                    }}
                    title="Export Team List to Excel (.xlsx)"
                  >
                    <FileSpreadsheet size={13} />
                    <span>Excel</span>
                    <Download size={11} />
                  </button>
                </div>
              </div>

              {/* Members Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: "10px",
                  maxHeight: "340px",
                  overflowY: "auto",
                  paddingRight: "4px",
                }}
              >
                {data.members
                  .filter((m) => {
                    const q = teamSearch.toLowerCase();
                    return (
                      !q ||
                      (m.name && m.name.toLowerCase().includes(q)) ||
                      (m.emp_code && m.emp_code.toLowerCase().includes(q)) ||
                      (m.designation && m.designation.toLowerCase().includes(q))
                    );
                  })
                  .slice(0, 30)
                  .map((m, idx) => {
                    const initials = (m.name || m.emp_code || "E")
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .substring(0, 2)
                      .toUpperCase();
                    return (
                      <div
                        key={idx}
                        style={{
                          padding: "10px 12px",
                          borderRadius: "10px",
                          background: "rgba(240, 247, 255, 0.8)",
                          border: "1px solid rgba(14, 73, 123, 0.18)",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #0e497b, #1d4ed8)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: "700",
                            flexShrink: 0,
                          }}
                        >
                          {initials}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "12.5px",
                              fontWeight: "700",
                              color: "#0f172a",
                            }}
                          >
                            <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                              {m.name}
                            </span>
                            <span
                              style={{
                                fontSize: "10px",
                                color: "#0e497b",
                                background: "rgba(14, 73, 123, 0.1)",
                                padding: "1px 5px",
                                borderRadius: "4px",
                                flexShrink: 0,
                                fontWeight: "600",
                              }}
                            >
                              {m.emp_code}
                            </span>
                          </div>

                          <div
                            style={{
                              fontSize: "11px",
                              color: "#64748b",
                              marginTop: "1px",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {m.designation || "-"}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginTop: "3px",
                              fontSize: "11px",
                              color: "#0e497b",
                            }}
                          >
                            {m.email && m.email !== "-" && (
                              <a
                                href={`mailto:${m.email}`}
                                style={{
                                  color: "#0e497b",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "3px",
                                  textDecoration: "none",
                                  fontWeight: "600",
                                }}
                                title={m.email}
                              >
                                <Mail size={11} /> Email
                              </a>
                            )}
                            {m.phone && m.phone !== "-" && (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", color: "#475569" }}>
                                <Phone size={11} /> {m.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 2. WIDGET: Attendance Card                          */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "attendance_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "14px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
                boxShadow: "0 4px 14px rgba(14, 73, 123, 0.08)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Clock size={16} color="#0e497b" />
                  <span style={{ fontWeight: "700", color: "#0e497b", fontSize: "13px" }}>
                    Attendance Log {data.emp_name ? `• ${data.emp_name}` : ""} ({data.date})
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    background:
                      data.status === "Present" || data.status === "PRESENT"
                        ? "rgba(16, 185, 129, 0.15)"
                        : data.status === "LATE" || data.is_late
                          ? "rgba(245, 158, 11, 0.15)"
                          : "rgba(239, 68, 68, 0.12)",
                    color:
                      data.status === "Present" || data.status === "PRESENT"
                        ? "#059669"
                        : data.status === "LATE" || data.is_late
                          ? "#d97706"
                          : "#dc2626",
                  }}
                >
                  {data.status}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", textAlign: "center" }}>
                <div style={{ padding: "8px", borderRadius: "10px", background: "rgba(240, 247, 255, 0.85)", border: "1px solid rgba(14, 73, 123, 0.18)" }}>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>Check-In</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#0e497b", marginTop: "2px" }}>
                    {data.check_in || "-"}
                  </div>
                </div>
                <div style={{ padding: "8px", borderRadius: "10px", background: "rgba(240, 247, 255, 0.85)", border: "1px solid rgba(14, 73, 123, 0.18)" }}>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>Check-Out</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#0e497b", marginTop: "2px" }}>
                    {data.check_out || "-"}
                  </div>
                </div>
                <div style={{ padding: "8px", borderRadius: "10px", background: "rgba(240, 247, 255, 0.85)", border: "1px solid rgba(14, 73, 123, 0.18)" }}>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>Total Work</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#059669", marginTop: "2px" }}>
                    {data.work_hours || "00:00"}
                  </div>
                </div>
              </div>

              {/* Single Day Excel Download button */}
              <div style={{ marginTop: "10px", display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={() => handleDownloadSingleAttendanceExcel(data)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    background: "rgba(16, 185, 129, 0.12)",
                    color: "#059669",
                    border: "1px solid rgba(16, 185, 129, 0.3)",
                    borderRadius: "8px",
                    padding: "4px 10px",
                    fontSize: "11px",
                    fontWeight: "700",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  title="Download today's attendance record as Excel"
                >
                  <FileSpreadsheet size={13} />
                  <span>Download Excel (.xlsx)</span>
                  <Download size={11} />
                </button>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 2b. WIDGET: Attendance Range History (Multi-Day)    */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "attendance_range_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                borderRadius: "16px",
                background: "linear-gradient(180deg, #ffffff 0%, rgba(240, 247, 255, 0.6) 100%)",
                border: "1px solid rgba(14, 73, 123, 0.22)",
                boxShadow: "0 6px 20px rgba(14, 73, 123, 0.09)",
              }}
            >
              {/* Card Header with Details & Download Excel Button */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "14px",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        background: "linear-gradient(135deg, #0e497b, #1d4ed8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                      }}
                    >
                      <Calendar size={17} />
                    </div>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: "700", color: "#0e497b" }}>
                        Attendance History {data.emp_name ? `• ${data.emp_name}` : ""}
                        <span
                          style={{
                            fontSize: "11px",
                            background: "rgba(14, 73, 123, 0.1)",
                            color: "#0e497b",
                            padding: "2px 7px",
                            borderRadius: "6px",
                            marginLeft: "8px",
                            fontWeight: "600",
                          }}
                        >
                          {data.emp_code}
                        </span>
                      </div>
                      <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>
                        {data.department && data.department !== "-" && `${data.department} • `}
                        {data.designation && data.designation !== "-" && `${data.designation} • `}
                        <span style={{ fontWeight: "600", color: "#0e497b" }}>
                          {data.start_date} to {data.end_date}
                        </span>{" "}
                        ({data.total_days} Days)
                      </div>
                    </div>
                  </div>
                </div>

                {/* Primary Excel Download Button */}
                <button
                  onClick={() => handleDownloadAttendanceRangeExcel(data)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "10px",
                    padding: "8px 14px",
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: "pointer",
                    boxShadow: "0 3px 10px rgba(16, 185, 129, 0.28)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 5px 14px rgba(16, 185, 129, 0.38)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 3px 10px rgba(16, 185, 129, 0.28)";
                  }}
                  title="Download full 30-day attendance history as Excel (.xlsx)"
                >
                  <FileSpreadsheet size={15} />
                  <span>Download Excel (.xlsx)</span>
                  <Download size={13} style={{ marginLeft: "2px" }} />
                </button>
              </div>

              {/* KPI Metrics Summary Grid */}
              {data.summary && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                    gap: "8px",
                    marginBottom: "14px",
                  }}
                >
                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(16, 185, 129, 0.1)",
                      border: "1px solid rgba(16, 185, 129, 0.25)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#065f46", fontWeight: "600" }}>Present Days</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#059669", marginTop: "2px" }}>
                      {data.summary.present_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#059669" }}>
                      {data.summary.attendance_rate || 0}% Rate
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(239, 68, 68, 0.08)",
                      border: "1px solid rgba(239, 68, 68, 0.2)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#991b1b", fontWeight: "600" }}>Absent / Unmarked</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#dc2626", marginTop: "2px" }}>
                      {data.summary.absent_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#dc2626" }}>Days</div>
                  </div>

                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(245, 158, 11, 0.08)",
                      border: "1px solid rgba(245, 158, 11, 0.2)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#92400e", fontWeight: "600" }}>Late Arrivals</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#d97706", marginTop: "2px" }}>
                      {data.summary.late_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#d97706" }}>Times</div>
                  </div>

                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(99, 102, 241, 0.08)",
                      border: "1px solid rgba(99, 102, 241, 0.2)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#3730a3", fontWeight: "600" }}>Half Days</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#4f46e5", marginTop: "2px" }}>
                      {data.summary.half_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#4f46e5" }}>Days</div>
                  </div>

                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(14, 73, 123, 0.08)",
                      border: "1px solid rgba(14, 73, 123, 0.2)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#0e497b", fontWeight: "600" }}>Sundays / Offs</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#0e497b", marginTop: "2px" }}>
                      {data.summary.sundays_holidays || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#0e497b" }}>Offs</div>
                  </div>

                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(14, 116, 144, 0.08)",
                      border: "1px solid rgba(14, 116, 144, 0.2)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#155e75", fontWeight: "600" }}>Total Logged</div>
                    <div style={{ fontSize: "14px", fontWeight: "800", color: "#0891b2", marginTop: "4px" }}>
                      {data.summary.total_work_time || "0h"}
                    </div>
                    <div style={{ fontSize: "10px", color: "#0891b2" }}>
                      Avg: {data.summary.avg_daily_hours || "-"}
                    </div>
                  </div>
                </div>
              )}

              {/* Table Controls: Search & Status Pills & Collapse Toggle */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "8px",
                  paddingBottom: "10px",
                  borderBottom: "1px solid rgba(14, 73, 123, 0.12)",
                  marginBottom: "10px",
                }}
              >
                {/* Status Pills */}
                <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                  {[
                    { key: "ALL", label: `All (${(data.records || []).length})` },
                    { key: "PRESENT", label: "Present" },
                    { key: "LATE", label: "Late" },
                    { key: "ABSENT", label: "Absent" },
                    { key: "HALFDAY", label: "Half Day" },
                    { key: "OFF", label: "Offs" },
                  ].map((tab) => {
                    const active = attRangeFilter === tab.key;
                    return (
                      <button
                        key={tab.key}
                        onClick={() => setAttRangeFilter(tab.key)}
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          padding: "3px 9px",
                          borderRadius: "14px",
                          border: active ? "1px solid #0e497b" : "1px solid #cbd5e1",
                          background: active ? "#0e497b" : "#ffffff",
                          color: active ? "#ffffff" : "#475569",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {/* Search input */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "#ffffff",
                      borderRadius: "18px",
                      padding: "2px 8px",
                      border: "1px solid rgba(14, 73, 123, 0.25)",
                    }}
                  >
                    <Search size={12} color="#64748b" style={{ marginRight: "4px" }} />
                    <input
                      type="text"
                      placeholder="Search date, remark..."
                      value={attRangeSearch}
                      onChange={(e) => setAttRangeSearch(e.target.value)}
                      style={{
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        color: "#0f172a",
                        fontSize: "11.5px",
                        width: "120px",
                      }}
                    />
                  </div>

                  {/* Collapse / Expand Toggle */}
                  <button
                    onClick={() => setIsRangeTableOpen(!isRangeTableOpen)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                      fontSize: "11px",
                      fontWeight: "600",
                      background: "rgba(14, 73, 123, 0.08)",
                      border: "none",
                      color: "#0e497b",
                      padding: "4px 8px",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                  >
                    {isRangeTableOpen ? (
                      <>
                        <span>Hide Table</span>
                        <ChevronUp size={13} />
                      </>
                    ) : (
                      <>
                        <span>Show Table</span>
                        <ChevronDown size={13} />
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Day-by-Day Table */}
              {isRangeTableOpen && (
                <div
                  style={{
                    maxHeight: "360px",
                    overflowY: "auto",
                    borderRadius: "10px",
                    border: "1px solid rgba(14, 73, 123, 0.15)",
                    background: "#ffffff",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "11.5px",
                      textAlign: "left",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: "rgba(240, 247, 255, 0.95)",
                          borderBottom: "1px solid rgba(14, 73, 123, 0.2)",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Date</th>
                        <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Day</th>
                        <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Status</th>
                        <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Check-In</th>
                        <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Check-Out</th>
                        <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Work Hours</th>
                        <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.records || [])
                        .filter((rec) => {
                          const q = attRangeSearch.toLowerCase();
                          const matchesSearch =
                            !q ||
                            (rec.date && rec.date.toLowerCase().includes(q)) ||
                            (rec.day && rec.day.toLowerCase().includes(q)) ||
                            (rec.status && rec.status.toLowerCase().includes(q)) ||
                            (rec.remarks && rec.remarks.toLowerCase().includes(q));

                          if (!matchesSearch) return false;

                          if (attRangeFilter === "PRESENT") {
                            return rec.raw_status === "PRESENT" || rec.status === "Present";
                          }
                          if (attRangeFilter === "LATE") {
                            return rec.is_late || rec.raw_status === "LATE";
                          }
                          if (attRangeFilter === "ABSENT") {
                            return rec.raw_status === "ABSENT" || rec.status.includes("Absent");
                          }
                          if (attRangeFilter === "HALFDAY") {
                            return rec.raw_status === "HALFDAY_LEAVE" || rec.status.includes("Half Day");
                          }
                          if (attRangeFilter === "OFF") {
                            return (
                              ["SUNDAY", "WEEKOFF", "HOLIDAY"].includes(rec.raw_status) ||
                              rec.status.includes("Sunday") ||
                              rec.status.includes("Holiday")
                            );
                          }
                          return true;
                        })
                        .map((rec, rIdx) => {
                          const isPresent = rec.status === "Present" || rec.raw_status === "PRESENT";
                          const isLate = rec.is_late || rec.raw_status === "LATE";
                          const isHalfDay = rec.raw_status === "HALFDAY_LEAVE" || rec.status.includes("Half Day");
                          const isSunday = rec.raw_status === "SUNDAY" || rec.status.includes("Sunday");
                          const isHoliday = rec.raw_status === "HOLIDAY" || rec.status.includes("Holiday");

                          let badgeBg = "rgba(239, 68, 68, 0.1)";
                          let badgeColor = "#dc2626";
                          if (isPresent) {
                            badgeBg = "rgba(16, 185, 129, 0.12)";
                            badgeColor = "#059669";
                          } else if (isLate) {
                            badgeBg = "rgba(245, 158, 11, 0.15)";
                            badgeColor = "#d97706";
                          } else if (isHalfDay) {
                            badgeBg = "rgba(99, 102, 241, 0.12)";
                            badgeColor = "#4f46e5";
                          } else if (isSunday || isHoliday) {
                            badgeBg = "rgba(100, 116, 139, 0.12)";
                            badgeColor = "#475569";
                          }

                          return (
                            <tr
                              key={rIdx}
                              style={{
                                borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                                background: rIdx % 2 === 0 ? "#ffffff" : "rgba(248, 250, 252, 0.6)",
                              }}
                            >
                              <td style={{ padding: "7px 10px", fontWeight: "600", color: "#1e293b", whiteSpace: "nowrap" }}>
                                {rec.date}
                              </td>
                              <td style={{ padding: "7px 10px", color: "#64748b", whiteSpace: "nowrap" }}>
                                {rec.day}
                              </td>
                              <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                                <span
                                  style={{
                                    fontSize: "10.5px",
                                    fontWeight: "700",
                                    padding: "2px 7px",
                                    borderRadius: "10px",
                                    background: badgeBg,
                                    color: badgeColor,
                                  }}
                                >
                                  {rec.status}
                                </span>
                              </td>
                              <td style={{ padding: "7px 10px", color: rec.check_in !== "-" ? "#0e497b" : "#94a3b8", fontWeight: rec.check_in !== "-" ? "600" : "400", whiteSpace: "nowrap" }}>
                                {rec.check_in}
                              </td>
                              <td style={{ padding: "7px 10px", color: rec.check_out !== "-" ? "#0e497b" : "#94a3b8", fontWeight: rec.check_out !== "-" ? "600" : "400", whiteSpace: "nowrap" }}>
                                {rec.check_out}
                              </td>
                              <td style={{ padding: "7px 10px", fontWeight: "700", color: rec.work_hours !== "00:00" ? "#059669" : "#94a3b8", whiteSpace: "nowrap" }}>
                                {rec.work_hours}
                              </td>
                              <td style={{ padding: "7px 10px", color: "#475569", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rec.remarks}>
                                {rec.remarks || "-"}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 2c. WIDGET: Personal Month-Wise Daily Attendance    */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "monthly_daily_attendance_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                borderRadius: "16px",
                background: "linear-gradient(180deg, #ffffff 0%, rgba(240, 247, 255, 0.6) 100%)",
                border: "1px solid rgba(14, 73, 123, 0.22)",
                boxShadow: "0 6px 20px rgba(14, 73, 123, 0.09)",
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "14px",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #0e497b, #1d4ed8)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      boxShadow: "0 2px 8px rgba(14, 73, 123, 0.25)",
                    }}
                  >
                    <Calendar size={18} />
                  </div>
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "700", color: "#0e497b" }}>
                      Daily Attendance • {data.emp_name || "Employee"}
                      <span
                        style={{
                          fontSize: "11px",
                          background: "rgba(14, 73, 123, 0.1)",
                          color: "#0e497b",
                          padding: "2px 7px",
                          borderRadius: "6px",
                          marginLeft: "8px",
                          fontWeight: "600",
                        }}
                      >
                        {data.emp_code}
                      </span>
                    </div>
                    <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>
                      {data.department && data.department !== "-" && `${data.department} • `}
                      {data.designation && data.designation !== "-" && `${data.designation} • `}
                      <span style={{ fontWeight: "700", color: "#0e497b" }}>
                        {data.month_name || ""} {data.year || ""}
                      </span>{" "}
                      ({data.summary ? data.summary.total_calendar_days : (data.records || []).length} Calendar Days)
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleDownloadMyMonthlyDailyAttendanceExcel(data)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "10px",
                    padding: "8px 14px",
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: "pointer",
                    boxShadow: "0 3px 10px rgba(16, 185, 129, 0.28)",
                    transition: "all 0.2s ease",
                  }}
                  title="Download month-wise daily attendance as Excel (.xlsx)"
                >
                  <FileSpreadsheet size={15} />
                  <span>Download Excel (.xlsx)</span>
                  <Download size={13} style={{ marginLeft: "2px" }} />
                </button>
              </div>

              {/* KPI Summary Grid */}
              {data.summary && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(95px, 1fr))",
                    gap: "8px",
                    marginBottom: "14px",
                  }}
                >
                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(16, 185, 129, 0.1)",
                      border: "1px solid rgba(16, 185, 129, 0.25)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#065f46", fontWeight: "600" }}>Present Days</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#059669", marginTop: "2px" }}>
                      {data.summary.present_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#059669" }}>
                      {data.summary.attendance_rate || 0}% Rate
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(14, 73, 123, 0.08)",
                      border: "1px solid rgba(14, 73, 123, 0.2)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#0e497b", fontWeight: "600" }}>Working Days</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#0e497b", marginTop: "2px" }}>
                      {data.summary.working_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#64748b" }}>In Month</div>
                  </div>

                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(239, 68, 68, 0.08)",
                      border: "1px solid rgba(239, 68, 68, 0.2)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#991b1b", fontWeight: "600" }}>Absent Days</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#dc2626", marginTop: "2px" }}>
                      {data.summary.absent_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#dc2626" }}>Unmarked</div>
                  </div>

                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(245, 158, 11, 0.1)",
                      border: "1px solid rgba(245, 158, 11, 0.25)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#92400e", fontWeight: "600" }}>Late Arrivals</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#d97706", marginTop: "2px" }}>
                      {data.summary.late_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#b45309" }}>Times</div>
                  </div>

                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(99, 102, 241, 0.1)",
                      border: "1px solid rgba(99, 102, 241, 0.25)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#3730a3", fontWeight: "600" }}>Half Days</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#4f46e5", marginTop: "2px" }}>
                      {data.summary.half_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#4f46e5" }}>Days</div>
                  </div>

                  <div
                    style={{
                      padding: "8px 10px",
                      borderRadius: "10px",
                      background: "rgba(100, 116, 139, 0.1)",
                      border: "1px solid rgba(100, 116, 139, 0.25)",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "10.5px", color: "#334155", fontWeight: "600" }}>Off Days</div>
                    <div style={{ fontSize: "16px", fontWeight: "800", color: "#475569", marginTop: "2px" }}>
                      {data.summary.off_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#64748b" }}>Sun & Holidays</div>
                  </div>
                </div>
              )}

              {/* Filter Tabs & Search */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                  marginBottom: "10px",
                }}
              >
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                  {[
                    { key: "ALL", label: "All Days" },
                    { key: "PRESENT", label: "Present" },
                    { key: "LATE", label: "Late In" },
                    { key: "HALFDAY", label: "Half Day" },
                    { key: "ABSENT", label: "Absent" },
                    { key: "OFF", label: "Off Days" },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setMonthlyAttFilter(tab.key)}
                      style={{
                        padding: "4px 10px",
                        fontSize: "11px",
                        fontWeight: monthlyAttFilter === tab.key ? "700" : "500",
                        borderRadius: "8px",
                        border: "1px solid",
                        borderColor: monthlyAttFilter === tab.key ? "#0e497b" : "rgba(14, 73, 123, 0.2)",
                        background: monthlyAttFilter === tab.key ? "linear-gradient(135deg, #0e497b, #1d4ed8)" : "#ffffff",
                        color: monthlyAttFilter === tab.key ? "#ffffff" : "#475569",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "#ffffff",
                    borderRadius: "16px",
                    padding: "3px 10px",
                    border: "1px solid #cbd5e1",
                  }}
                >
                  <Search size={12} color="#64748b" style={{ marginRight: "4px" }} />
                  <input
                    type="text"
                    placeholder="Search date, status..."
                    value={monthlyAttSearch}
                    onChange={(e) => setMonthlyAttSearch(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      fontSize: "11px",
                      width: "120px",
                      background: "transparent",
                      color: "#0f172a",
                    }}
                  />
                </div>
              </div>

              {/* Day-by-Day Calendar Log Table */}
              <div
                style={{
                  maxHeight: "360px",
                  overflowY: "auto",
                  borderRadius: "10px",
                  border: "1px solid rgba(14, 73, 123, 0.15)",
                  background: "#ffffff",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "11.5px",
                    textAlign: "left",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "rgba(240, 247, 255, 0.95)",
                        borderBottom: "1px solid rgba(14, 73, 123, 0.2)",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Date</th>
                      <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Day</th>
                      <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Status</th>
                      <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Check-In</th>
                      <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Check-Out</th>
                      <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Work Hours</th>
                      <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.records || [])
                      .filter((rec) => {
                        const q = monthlyAttSearch.toLowerCase();
                        const matchesSearch =
                          !q ||
                          (rec.formatted_date && rec.formatted_date.toLowerCase().includes(q)) ||
                          (rec.date && rec.date.toLowerCase().includes(q)) ||
                          (rec.day && rec.day.toLowerCase().includes(q)) ||
                          (rec.status && rec.status.toLowerCase().includes(q)) ||
                          (rec.remarks && rec.remarks.toLowerCase().includes(q));

                        if (!matchesSearch) return false;

                        if (monthlyAttFilter === "PRESENT") {
                          return rec.status === "Present";
                        }
                        if (monthlyAttFilter === "LATE") {
                          return rec.is_late || rec.status.includes("Late");
                        }
                        if (monthlyAttFilter === "HALFDAY") {
                          return rec.status.includes("Half Day");
                        }
                        if (monthlyAttFilter === "ABSENT") {
                          return rec.status === "Absent";
                        }
                        if (monthlyAttFilter === "OFF") {
                          return rec.is_off_day || rec.status.includes("Sunday") || rec.status.includes("Holiday") || rec.status.includes("Off");
                        }
                        return true;
                      })
                      .map((rec, rIdx) => {
                        const isPres = rec.status === "Present";
                        const isLt = rec.is_late || rec.status.includes("Late");
                        const isHf = rec.status.includes("Half Day");
                        const isOff = rec.is_off_day || rec.status.includes("Sunday") || rec.status.includes("Holiday");

                        let badgeBg = "rgba(239, 68, 68, 0.1)";
                        let badgeColor = "#dc2626";
                        if (isPres) {
                          badgeBg = "rgba(16, 185, 129, 0.12)";
                          badgeColor = "#059669";
                        } else if (isLt) {
                          badgeBg = "rgba(245, 158, 11, 0.15)";
                          badgeColor = "#d97706";
                        } else if (isHf) {
                          badgeBg = "rgba(99, 102, 241, 0.12)";
                          badgeColor = "#4f46e5";
                        } else if (isOff) {
                          badgeBg = "rgba(100, 116, 139, 0.12)";
                          badgeColor = "#475569";
                        }

                        return (
                          <tr
                            key={rIdx}
                            style={{
                              borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                              background: rIdx % 2 === 0 ? "#ffffff" : "rgba(248, 250, 252, 0.6)",
                            }}
                          >
                            <td style={{ padding: "7px 10px", fontWeight: "600", color: "#1e293b", whiteSpace: "nowrap" }}>
                              {rec.formatted_date || rec.date}
                            </td>
                            <td style={{ padding: "7px 10px", color: "#64748b", whiteSpace: "nowrap" }}>
                              {rec.day}
                            </td>
                            <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                              <span
                                style={{
                                  fontSize: "10.5px",
                                  fontWeight: "700",
                                  padding: "2px 7px",
                                  borderRadius: "10px",
                                  background: badgeBg,
                                  color: badgeColor,
                                }}
                              >
                                {rec.status}
                              </span>
                            </td>
                            <td style={{ padding: "7px 10px", color: rec.check_in !== "-" ? "#0e497b" : "#94a3b8", fontWeight: rec.check_in !== "-" ? "600" : "400", whiteSpace: "nowrap" }}>
                              {rec.check_in}
                            </td>
                            <td style={{ padding: "7px 10px", color: rec.check_out !== "-" ? "#0e497b" : "#94a3b8", fontWeight: rec.check_out !== "-" ? "600" : "400", whiteSpace: "nowrap" }}>
                              {rec.check_out}
                            </td>
                            <td style={{ padding: "7px 10px", fontWeight: "700", color: rec.work_hours !== "00:00" ? "#059669" : "#94a3b8", whiteSpace: "nowrap" }}>
                              {rec.work_hours}
                            </td>
                            <td style={{ padding: "7px 10px", color: "#475569", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rec.remarks}>
                              {rec.remarks || "-"}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 2d. WIDGET: Organisation Monthly Attendance Table   */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "table_organisation_monthly_attendance" && data && (() => {
            const summary = data.summary || {};
            const dailyTrends = data.daily_trends || [];
            const allEmpRecords = data.employee_records || [];

            const filteredEmps = allEmpRecords.filter((e) => {
              if (orgAttSearch.trim()) {
                const q = orgAttSearch.toLowerCase().trim();
                const matchName = (e.emp_name || "").toLowerCase().includes(q);
                const matchCode = (e.emp_code || "").toLowerCase().includes(q);
                const matchDept = (e.department || "").toLowerCase().includes(q);
                const matchDesig = (e.designation || "").toLowerCase().includes(q);
                if (!matchName && !matchCode && !matchDept && !matchDesig) return false;
              }
              return true;
            });

            const pageSize = 12;
            const totalPages = Math.max(1, Math.ceil(filteredEmps.length / pageSize));
            const safePage = Math.min(orgAttPage, totalPages);
            const pagedEmps = filteredEmps.slice((safePage - 1) * pageSize, safePage * pageSize);

            return (
              <div
                style={{
                  marginTop: "12px",
                  padding: "16px",
                  borderRadius: "16px",
                  background: "linear-gradient(180deg, #ffffff 0%, rgba(240, 247, 255, 0.6) 100%)",
                  border: "1px solid rgba(14, 73, 123, 0.22)",
                  boxShadow: "0 6px 20px rgba(14, 73, 123, 0.09)",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "14px",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #0e497b, #1d4ed8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        boxShadow: "0 2px 8px rgba(14, 73, 123, 0.25)",
                      }}
                    >
                      <Users size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: "700", color: "#0e497b" }}>
                        {data.category_title || "Organization Monthly Attendance"}
                        <span
                          style={{
                            fontSize: "11px",
                            background: "rgba(14, 73, 123, 0.1)",
                            color: "#0e497b",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            marginLeft: "8px",
                            fontWeight: "700",
                          }}
                        >
                          {summary.total_employees || allEmpRecords.length} Active Staff
                        </span>
                      </div>
                      <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>
                        Month of <span style={{ fontWeight: "700", color: "#0e497b" }}>{data.month_name} {data.year}</span> • {summary.working_days || 0} Working Days • {summary.off_days || 0} Off Days
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleDownloadOrganisationMonthlyAttendanceExcel(data)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "linear-gradient(135deg, #059669, #10b981)",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "10px",
                      padding: "8px 14px",
                      fontSize: "12px",
                      fontWeight: "700",
                      cursor: "pointer",
                      boxShadow: "0 3px 10px rgba(16, 185, 129, 0.28)",
                      transition: "all 0.2s ease",
                    }}
                    title="Export Organization Monthly Attendance to Excel (.xlsx)"
                  >
                    <FileSpreadsheet size={15} />
                    <span>Download Excel (.xlsx)</span>
                    <Download size={13} style={{ marginLeft: "2px" }} />
                  </button>
                </div>

                {/* KPI Metrics Summary Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                    gap: "8px",
                    marginBottom: "14px",
                  }}
                >
                  <div style={{ padding: "8px 10px", borderRadius: "10px", background: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.25)", textAlign: "center" }}>
                    <div style={{ fontSize: "10.5px", color: "#065f46", fontWeight: "600" }}>Avg Attendance</div>
                    <div style={{ fontSize: "17px", fontWeight: "800", color: "#059669", marginTop: "2px" }}>
                      {summary.avg_attendance_rate || 0}%
                    </div>
                    <div style={{ fontSize: "10px", color: "#059669" }}>Across Org</div>
                  </div>

                  <div style={{ padding: "8px 10px", borderRadius: "10px", background: "rgba(14, 73, 123, 0.08)", border: "1px solid rgba(14, 73, 123, 0.2)", textAlign: "center" }}>
                    <div style={{ fontSize: "10.5px", color: "#0e497b", fontWeight: "600" }}>Present Man-Days</div>
                    <div style={{ fontSize: "17px", fontWeight: "800", color: "#0e497b", marginTop: "2px" }}>
                      {(summary.total_present_man_days || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: "10px", color: "#64748b" }}>Accumulated</div>
                  </div>

                  <div style={{ padding: "8px 10px", borderRadius: "10px", background: "rgba(239, 68, 68, 0.08)", border: "1px solid rgba(239, 68, 68, 0.2)", textAlign: "center" }}>
                    <div style={{ fontSize: "10.5px", color: "#991b1b", fontWeight: "600" }}>Absent Man-Days</div>
                    <div style={{ fontSize: "17px", fontWeight: "800", color: "#dc2626", marginTop: "2px" }}>
                      {(summary.total_absent_man_days || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: "10px", color: "#dc2626" }}>Total Shortfall</div>
                  </div>

                  <div style={{ padding: "8px 10px", borderRadius: "10px", background: "rgba(100, 116, 139, 0.1)", border: "1px solid rgba(100, 116, 139, 0.25)", textAlign: "center" }}>
                    <div style={{ fontSize: "10.5px", color: "#334155", fontWeight: "600" }}>Off Days</div>
                    <div style={{ fontSize: "17px", fontWeight: "800", color: "#475569", marginTop: "2px" }}>
                      {summary.off_days || 0}
                    </div>
                    <div style={{ fontSize: "10px", color: "#64748b" }}>Sundays / Holidays</div>
                  </div>
                </div>

                {/* Tab Switcher: Daily Trends vs Employee Roster */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginBottom: "10px",
                    borderBottom: "1px solid rgba(14, 73, 123, 0.15)",
                    paddingBottom: "8px",
                  }}
                >
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button
                      onClick={() => setOrgAttTab("TRENDS")}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: orgAttTab === "TRENDS" ? "700" : "500",
                        borderRadius: "8px",
                        border: "1px solid",
                        borderColor: orgAttTab === "TRENDS" ? "#0e497b" : "rgba(14, 73, 123, 0.2)",
                        background: orgAttTab === "TRENDS" ? "linear-gradient(135deg, #0e497b, #1d4ed8)" : "#ffffff",
                        color: orgAttTab === "TRENDS" ? "#ffffff" : "#475569",
                        cursor: "pointer",
                      }}
                    >
                      📅 Daily Attendance Trends ({dailyTrends.length} Days)
                    </button>
                    <button
                      onClick={() => setOrgAttTab("ROSTER")}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: orgAttTab === "ROSTER" ? "700" : "500",
                        borderRadius: "8px",
                        border: "1px solid",
                        borderColor: orgAttTab === "ROSTER" ? "#0e497b" : "rgba(14, 73, 123, 0.2)",
                        background: orgAttTab === "ROSTER" ? "linear-gradient(135deg, #0e497b, #1d4ed8)" : "#ffffff",
                        color: orgAttTab === "ROSTER" ? "#ffffff" : "#475569",
                        cursor: "pointer",
                      }}
                    >
                      👥 All Employees Monthly Roster ({allEmpRecords.length})
                    </button>
                  </div>

                  {orgAttTab === "ROSTER" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        background: "#ffffff",
                        borderRadius: "16px",
                        padding: "3px 10px",
                        border: "1px solid #cbd5e1",
                      }}
                    >
                      <Search size={12} color="#64748b" style={{ marginRight: "4px" }} />
                      <input
                        type="text"
                        placeholder="Search employee, dept..."
                        value={orgAttSearch}
                        onChange={(e) => { setOrgAttSearch(e.target.value); setOrgAttPage(1); }}
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "11px",
                          width: "140px",
                          background: "transparent",
                          color: "#0f172a",
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Content Panel */}
                {orgAttTab === "TRENDS" ? (
                  <div
                    style={{
                      maxHeight: "340px",
                      overflowY: "auto",
                      borderRadius: "10px",
                      border: "1px solid rgba(14, 73, 123, 0.15)",
                      background: "#ffffff",
                    }}
                  >
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11.5px", textAlign: "left" }}>
                      <thead>
                        <tr
                          style={{
                            background: "rgba(240, 247, 255, 0.95)",
                            borderBottom: "1px solid rgba(14, 73, 123, 0.2)",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Date</th>
                          <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Day</th>
                          <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Type</th>
                          <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700", textAlign: "center" }}>Present</th>
                          <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700", textAlign: "center" }}>Absent</th>
                          <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700", textAlign: "center" }}>Late</th>
                          <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700", textAlign: "right" }}>Attendance Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailyTrends.map((dt, idx) => (
                          <tr
                            key={idx}
                            style={{
                              borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                              background: dt.is_off_day ? "rgba(241, 245, 249, 0.5)" : (idx % 2 === 0 ? "#ffffff" : "rgba(248, 250, 252, 0.6)"),
                            }}
                          >
                            <td style={{ padding: "7px 10px", fontWeight: "600", color: "#1e293b", whiteSpace: "nowrap" }}>
                              {dt.date}
                            </td>
                            <td style={{ padding: "7px 10px", color: "#64748b", whiteSpace: "nowrap" }}>
                              {dt.day}
                            </td>
                            <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                              {dt.is_off_day ? (
                                <span style={{ fontSize: "10.5px", padding: "2px 7px", borderRadius: "8px", background: "rgba(100, 116, 139, 0.12)", color: "#475569", fontWeight: "600" }}>
                                  🏖️ {dt.off_reason || "Off Day"}
                                </span>
                              ) : (
                                <span style={{ fontSize: "10.5px", padding: "2px 7px", borderRadius: "8px", background: "rgba(14, 73, 123, 0.08)", color: "#0e497b", fontWeight: "600" }}>
                                  Working Day
                                </span>
                              )}
                            </td>
                            <td style={{ padding: "7px 10px", textAlign: "center", fontWeight: "700", color: "#059669" }}>
                              {dt.present || 0}
                            </td>
                            <td style={{ padding: "7px 10px", textAlign: "center", fontWeight: "700", color: "#dc2626" }}>
                              {dt.absent || 0}
                            </td>
                            <td style={{ padding: "7px 10px", textAlign: "center", fontWeight: "600", color: "#d97706" }}>
                              {dt.late || 0}
                            </td>
                            <td style={{ padding: "7px 10px", textAlign: "right" }}>
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "700",
                                  padding: "2px 8px",
                                  borderRadius: "10px",
                                  background: (dt.attendance_rate || 0) >= 50 ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.1)",
                                  color: (dt.attendance_rate || 0) >= 50 ? "#059669" : "#dc2626",
                                }}
                              >
                                {dt.attendance_rate || 0}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        maxHeight: "340px",
                        overflowY: "auto",
                        borderRadius: "10px",
                        border: "1px solid rgba(14, 73, 123, 0.15)",
                        background: "#ffffff",
                      }}
                    >
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11.5px", textAlign: "left" }}>
                        <thead>
                          <tr
                            style={{
                              background: "rgba(240, 247, 255, 0.95)",
                              borderBottom: "1px solid rgba(14, 73, 123, 0.2)",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                            }}
                          >
                            <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Employee</th>
                            <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700" }}>Department</th>
                            <th style={{ padding: "8px 8px", color: "#0e497b", fontWeight: "700", textAlign: "center" }}>Present</th>
                            <th style={{ padding: "8px 8px", color: "#0e497b", fontWeight: "700", textAlign: "center" }}>Absent</th>
                            <th style={{ padding: "8px 8px", color: "#0e497b", fontWeight: "700", textAlign: "center" }}>Late</th>
                            <th style={{ padding: "8px 8px", color: "#0e497b", fontWeight: "700", textAlign: "center" }}>Off Days</th>
                            <th style={{ padding: "8px 10px", color: "#0e497b", fontWeight: "700", textAlign: "right" }}>Rate %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedEmps.length === 0 ? (
                            <tr>
                              <td colSpan="7" style={{ textAlign: "center", padding: "16px", color: "#64748b" }}>
                                No employees found matching "{orgAttSearch}".
                              </td>
                            </tr>
                          ) : (
                            pagedEmps.map((e, idx) => (
                              <tr
                                key={e.emp_code || idx}
                                style={{
                                  borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                                  background: idx % 2 === 0 ? "#ffffff" : "rgba(248, 250, 252, 0.6)",
                                }}
                              >
                                <td style={{ padding: "7px 10px" }}>
                                  <div style={{ fontWeight: "700", color: "#0f172a" }}>{e.emp_name}</div>
                                  <div style={{ fontSize: "10.5px", color: "#64748b" }}>{e.emp_code} • {e.designation || "-"}</div>
                                </td>
                                <td style={{ padding: "7px 10px", color: "#334155" }}>
                                  {e.department || "-"}
                                </td>
                                <td style={{ padding: "7px 8px", textAlign: "center", fontWeight: "700", color: "#059669" }}>
                                  {e.present_days}d
                                </td>
                                <td style={{ padding: "7px 8px", textAlign: "center", fontWeight: "600", color: "#dc2626" }}>
                                  {e.absent_days}d
                                </td>
                                <td style={{ padding: "7px 8px", textAlign: "center", fontWeight: "600", color: "#d97706" }}>
                                  {e.late_days}
                                </td>
                                <td style={{ padding: "7px 8px", textAlign: "center", color: "#64748b" }}>
                                  {e.off_days}d
                                </td>
                                <td style={{ padding: "7px 10px", textAlign: "right" }}>
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: "700",
                                      padding: "2px 8px",
                                      borderRadius: "10px",
                                      background: (e.attendance_rate || 0) >= 50 ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.1)",
                                      color: (e.attendance_rate || 0) >= 50 ? "#059669" : "#dc2626",
                                    }}
                                  >
                                    {e.attendance_rate}%
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: "10px",
                          paddingTop: "8px",
                          borderTop: "1px solid #f1f5f9",
                          fontSize: "11px",
                          color: "#64748b",
                        }}
                      >
                        <span>
                          Showing {(safePage - 1) * pageSize + 1} - {Math.min(safePage * pageSize, filteredEmps.length)} of {filteredEmps.length}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <button
                            disabled={safePage <= 1}
                            onClick={() => setOrgAttPage((p) => Math.max(1, p - 1))}
                            style={{
                              border: "1px solid #cbd5e1",
                              background: safePage <= 1 ? "#f8fafc" : "#ffffff",
                              color: safePage <= 1 ? "#94a3b8" : "#0e497b",
                              borderRadius: "6px",
                              padding: "2px 6px",
                              cursor: safePage <= 1 ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <ChevronLeft size={12} /> Prev
                          </button>
                          <span style={{ fontWeight: "700", color: "#0e497b" }}>
                            {safePage} / {totalPages}
                          </span>
                          <button
                            disabled={safePage >= totalPages}
                            onClick={() => setOrgAttPage((p) => Math.min(totalPages, p + 1))}
                            style={{
                              border: "1px solid #cbd5e1",
                              background: safePage >= totalPages ? "#f8fafc" : "#ffffff",
                              color: safePage >= totalPages ? "#94a3b8" : "#0e497b",
                              borderRadius: "6px",
                              padding: "2px 6px",
                              cursor: safePage >= totalPages ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Next <ChevronRight size={12} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ══════════════════════════════════════════════════ */}
          {/* 3. WIDGET: Team Attendance Donut Breakdown          */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "chart_donut" && data && (() => {
            const allRecords = data.records || [];
            const filteredRecords = allRecords.filter((m) => {
              const statusStr = (m.status || "").toUpperCase();
              const isPresent = statusStr.includes("PRESENT") || (m.check_in && m.check_in !== "-");
              const isAbsent = statusStr.includes("ABSENT") && !isPresent;
              const isLate = statusStr.includes("LATE");
              const isHalfDay = statusStr.includes("HALF");

              if (attStatusFilter === "PRESENT" && !isPresent) return false;
              if (attStatusFilter === "ABSENT" && !isAbsent) return false;
              if (attStatusFilter === "LATE" && !isLate) return false;
              if (attStatusFilter === "HALFDAY" && !isHalfDay) return false;

              if (attSearch.trim()) {
                const q = attSearch.toLowerCase().trim();
                const matchName = (m.emp_name || "").toLowerCase().includes(q);
                const matchCode = (m.emp_code || "").toLowerCase().includes(q);
                const matchDept = (m.department || "").toLowerCase().includes(q);
                const matchDesig = (m.designation || "").toLowerCase().includes(q);
                if (!matchName && !matchCode && !matchDept && !matchDesig) return false;
              }
              return true;
            });

            const pageSize = 12;
            const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
            const safePage = Math.min(attPage, totalPages);
            const pagedRecords = filteredRecords.slice((safePage - 1) * pageSize, safePage * pageSize);

            return (
              <div
                style={{
                  marginTop: "12px",
                  padding: "14px",
                  borderRadius: "14px",
                  background: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid rgba(14, 73, 123, 0.2)",
                  boxShadow: "0 4px 14px rgba(14, 73, 123, 0.08)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#64748b",
                  }}
                >
                  <span style={{ fontWeight: "700", color: "#0e497b" }}>
                    Attendance Breakdown ({data.date || "Today"})
                  </span>
                  <span style={{ color: "#0e497b", fontWeight: "700" }}>
                    Total: {data.total_team_size || allRecords.length} Employees
                  </span>
                </div>

                {/* KPI Metric Tiles */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px", textAlign: "center" }}>
                  <div
                    onClick={() => { setAttStatusFilter("PRESENT"); setAttPage(1); }}
                    style={{
                      padding: "8px",
                      borderRadius: "10px",
                      background: attStatusFilter === "PRESENT" ? "rgba(16, 185, 129, 0.22)" : "rgba(16, 185, 129, 0.12)",
                      border: attStatusFilter === "PRESENT" ? "2px solid #059669" : "1px solid rgba(16, 185, 129, 0.25)",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    title="Filter by Present"
                  >
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#059669" }}>{data.present || 0}</div>
                    <div style={{ fontSize: "11px", color: "#065f46", fontWeight: "600" }}>Present</div>
                  </div>
                  <div
                    onClick={() => { setAttStatusFilter("ABSENT"); setAttPage(1); }}
                    style={{
                      padding: "8px",
                      borderRadius: "10px",
                      background: attStatusFilter === "ABSENT" ? "rgba(239, 68, 68, 0.22)" : "rgba(239, 68, 68, 0.12)",
                      border: attStatusFilter === "ABSENT" ? "2px solid #dc2626" : "1px solid rgba(239, 68, 68, 0.25)",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    title="Filter by Absent"
                  >
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#dc2626" }}>{data.absent || 0}</div>
                    <div style={{ fontSize: "11px", color: "#991b1b", fontWeight: "600" }}>Absent</div>
                  </div>
                  <div
                    onClick={() => { setAttStatusFilter("LATE"); setAttPage(1); }}
                    style={{
                      padding: "8px",
                      borderRadius: "10px",
                      background: attStatusFilter === "LATE" ? "rgba(245, 158, 11, 0.22)" : "rgba(245, 158, 11, 0.12)",
                      border: attStatusFilter === "LATE" ? "2px solid #d97706" : "1px solid rgba(245, 158, 11, 0.25)",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    title="Filter by Late"
                  >
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#d97706" }}>{data.late || 0}</div>
                    <div style={{ fontSize: "11px", color: "#92400e", fontWeight: "600" }}>Late</div>
                  </div>
                  <div
                    onClick={() => { setAttStatusFilter("HALFDAY"); setAttPage(1); }}
                    style={{
                      padding: "8px",
                      borderRadius: "10px",
                      background: attStatusFilter === "HALFDAY" ? "rgba(14, 73, 123, 0.22)" : "rgba(14, 73, 123, 0.12)",
                      border: attStatusFilter === "HALFDAY" ? "2px solid #0e497b" : "1px solid rgba(14, 73, 123, 0.25)",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    title="Filter by Half Day"
                  >
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#0e497b" }}>{data.half_day || 0}</div>
                    <div style={{ fontSize: "11px", color: "#0e497b", fontWeight: "600" }}>Half Day</div>
                  </div>
                </div>

                {/* Filter Controls Row */}
                {allRecords.length > 0 && (
                  <div style={{ marginTop: "12px", borderTop: "1px solid #e2e8f0", paddingTop: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "8px",
                        marginBottom: "10px",
                      }}
                    >
                      {/* Filter tabs */}
                      <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                        {[
                          { key: "ALL", label: `All (${allRecords.length})` },
                          { key: "PRESENT", label: `🟢 Present (${data.present || 0})` },
                          { key: "ABSENT", label: `🔴 Absent (${data.absent || 0})` },
                          { key: "LATE", label: `🟡 Late (${data.late || 0})` },
                        ].map((tab) => (
                          <button
                            key={tab.key}
                            onClick={() => { setAttStatusFilter(tab.key); setAttPage(1); }}
                            style={{
                              padding: "4px 8px",
                              borderRadius: "8px",
                              fontSize: "11px",
                              fontWeight: "600",
                              cursor: "pointer",
                              border: "none",
                              background: attStatusFilter === tab.key ? "#0e497b" : "#f1f5f9",
                              color: attStatusFilter === tab.key ? "#ffffff" : "#475569",
                              transition: "all 0.15s ease",
                            }}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {/* Search Bar & Excel Download */}
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            background: "#ffffff",
                            borderRadius: "16px",
                            padding: "2px 8px",
                            border: "1px solid #cbd5e1",
                          }}
                        >
                          <Search size={12} color="#64748b" style={{ marginRight: "4px" }} />
                          <input
                            type="text"
                            placeholder="Filter name, code, dept..."
                            value={attSearch}
                            onChange={(e) => { setAttSearch(e.target.value); setAttPage(1); }}
                            style={{
                              border: "none",
                              outline: "none",
                              fontSize: "11px",
                              width: "120px",
                              background: "transparent",
                              color: "#0f172a",
                            }}
                          />
                        </div>
                        <button
                          onClick={() => handleDownloadTeamAttendanceExcel(data)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            background: "rgba(16, 185, 129, 0.12)",
                            color: "#059669",
                            border: "1px solid rgba(16, 185, 129, 0.3)",
                            borderRadius: "8px",
                            padding: "4px 8px",
                            fontSize: "11px",
                            fontWeight: "700",
                            cursor: "pointer",
                          }}
                          title="Download attendance report as Excel"
                        >
                          <FileSpreadsheet size={12} />
                          <span>Excel</span>
                          <Download size={10} />
                        </button>
                      </div>
                    </div>

                    {/* Member Records List */}
                    {pagedRecords.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "16px", color: "#64748b", fontSize: "12px" }}>
                        No records match the selected filter.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {pagedRecords.map((m, idx) => {
                          const isMbrPres = (m.status || "").toUpperCase().includes("PRESENT") || (m.check_in && m.check_in !== "-");
                          const isMbrLate = (m.status || "").toUpperCase().includes("LATE");
                          return (
                            <div
                              key={m.emp_code || idx}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "7px 10px",
                                borderRadius: "8px",
                                background: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                fontSize: "12px",
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: "700", color: "#0f172a" }}>
                                  {m.emp_name}{" "}
                                  <span style={{ fontWeight: "500", color: "#64748b", fontSize: "11px" }}>
                                    ({m.emp_code})
                                  </span>
                                </div>
                                <div style={{ color: "#64748b", fontSize: "11px", marginTop: "1px" }}>
                                  {m.designation || "-"} • <span style={{ color: "#0e497b" }}>{m.department || "-"}</span>
                                </div>
                              </div>

                              <div style={{ textAlign: "right" }}>
                                <span
                                  style={{
                                    fontSize: "10.5px",
                                    padding: "2px 8px",
                                    borderRadius: "10px",
                                    fontWeight: "700",
                                    background: isMbrPres
                                      ? "rgba(16, 185, 129, 0.15)"
                                      : isMbrLate
                                        ? "rgba(245, 158, 11, 0.15)"
                                        : "rgba(239, 68, 68, 0.12)",
                                    color: isMbrPres
                                      ? "#059669"
                                      : isMbrLate
                                        ? "#d97706"
                                        : "#dc2626",
                                  }}
                                >
                                  {m.status || (isMbrPres ? "Present" : "Absent")}
                                </span>
                                <div style={{ fontSize: "11px", color: "#334155", marginTop: "3px", fontWeight: "600" }}>
                                  In: {m.check_in || "-"} {m.check_out && m.check_out !== "-" ? `| Out: ${m.check_out}` : ""}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Pagination Bar */}
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: "10px",
                          paddingTop: "8px",
                          borderTop: "1px solid #f1f5f9",
                          fontSize: "11px",
                          color: "#64748b",
                        }}
                      >
                        <span>
                          Showing {(safePage - 1) * pageSize + 1} - {Math.min(safePage * pageSize, filteredRecords.length)} of {filteredRecords.length}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <button
                            disabled={safePage <= 1}
                            onClick={() => setAttPage((p) => Math.max(1, p - 1))}
                            style={{
                              border: "1px solid #cbd5e1",
                              background: safePage <= 1 ? "#f8fafc" : "#ffffff",
                              color: safePage <= 1 ? "#94a3b8" : "#0e497b",
                              borderRadius: "6px",
                              padding: "2px 6px",
                              cursor: safePage <= 1 ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <ChevronLeft size={12} /> Prev
                          </button>
                          <span style={{ fontWeight: "700", color: "#0e497b" }}>
                            {safePage} / {totalPages}
                          </span>
                          <button
                            disabled={safePage >= totalPages}
                            onClick={() => setAttPage((p) => Math.min(totalPages, p + 1))}
                            style={{
                              border: "1px solid #cbd5e1",
                              background: safePage >= totalPages ? "#f8fafc" : "#ffffff",
                              color: safePage >= totalPages ? "#94a3b8" : "#0e497b",
                              borderRadius: "6px",
                              padding: "2px 6px",
                              cursor: safePage >= totalPages ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Next <ChevronRight size={12} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ══════════════════════════════════════════════════ */}
          {/* 3b. WIDGET: Organisation Leave Balances Table       */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "table_leave_balances" && data && (() => {
            const allRecords = data.records || [];
            const filteredRecords = allRecords.filter((r) => {
              if (leaveBalSearch.trim()) {
                const q = leaveBalSearch.toLowerCase().trim();
                const matchName = (r.emp_name || "").toLowerCase().includes(q);
                const matchCode = (r.emp_code || "").toLowerCase().includes(q);
                const matchDept = (r.department || "").toLowerCase().includes(q);
                const matchDesig = (r.designation || "").toLowerCase().includes(q);
                if (!matchName && !matchCode && !matchDept && !matchDesig) return false;
              }
              return true;
            });

            const pageSize = 12;
            const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
            const safePage = Math.min(leaveBalPage, totalPages);
            const pagedRecords = filteredRecords.slice((safePage - 1) * pageSize, safePage * pageSize);

            return (
              <div
                style={{
                  marginTop: "12px",
                  padding: "14px",
                  borderRadius: "14px",
                  background: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid rgba(14, 73, 123, 0.2)",
                  boxShadow: "0 4px 14px rgba(14, 73, 123, 0.08)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Calendar size={16} color="#0e497b" />
                    <span style={{ fontSize: "13px", fontWeight: "700", color: "#0e497b" }}>
                      {data.category_title || "Organization Leave Balances"} ({data.year || new Date().getFullYear()})
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        background: "rgba(14, 73, 123, 0.1)",
                        color: "#0e497b",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontWeight: "700",
                      }}
                    >
                      {data.total_employees || allRecords.length} Employees
                    </span>
                  </div>

                  {/* Search and Excel Download */}
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        background: "#ffffff",
                        borderRadius: "16px",
                        padding: "2px 8px",
                        border: "1px solid #cbd5e1",
                      }}
                    >
                      <Search size={12} color="#64748b" style={{ marginRight: "4px" }} />
                      <input
                        type="text"
                        placeholder="Filter employee or dept..."
                        value={leaveBalSearch}
                        onChange={(e) => { setLeaveBalSearch(e.target.value); setLeaveBalPage(1); }}
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "11px",
                          width: "130px",
                          background: "transparent",
                          color: "#0f172a",
                        }}
                      />
                    </div>
                    <button
                      onClick={() => handleDownloadOrganisationLeaveBalancesExcel(data)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        background: "rgba(16, 185, 129, 0.12)",
                        color: "#059669",
                        border: "1px solid rgba(16, 185, 129, 0.3)",
                        borderRadius: "8px",
                        padding: "4px 8px",
                        fontSize: "11px",
                        fontWeight: "700",
                        cursor: "pointer",
                      }}
                      title="Download leave balances report as Excel"
                    >
                      <FileSpreadsheet size={12} />
                      <span>Excel (.xlsx)</span>
                      <Download size={10} />
                    </button>
                  </div>
                </div>

                {/* KPI metric cards (CL, SL, Off Days only) */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px", textAlign: "center", marginBottom: "12px" }}>
                  <div style={{ padding: "6px", borderRadius: "8px", background: "rgba(14, 73, 123, 0.1)", border: "1px solid rgba(14, 73, 123, 0.2)" }}>
                    <div style={{ fontSize: "10px", color: "#0e497b", fontWeight: "700" }}>Total Pool</div>
                    <div style={{ fontSize: "15px", fontWeight: "800", color: "#0e497b" }}>{data.total_leave_pool ?? 0}d</div>
                  </div>
                  <div style={{ padding: "6px", borderRadius: "8px", background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                    <div style={{ fontSize: "10px", color: "#1d4ed8", fontWeight: "700" }}>Casual (CL)</div>
                    <div style={{ fontSize: "15px", fontWeight: "800", color: "#1d4ed8" }}>{data.total_cl ?? 0}d</div>
                  </div>
                  <div style={{ padding: "6px", borderRadius: "8px", background: "rgba(20, 184, 166, 0.1)", border: "1px solid rgba(20, 184, 166, 0.2)" }}>
                    <div style={{ fontSize: "10px", color: "#0f766e", fontWeight: "700" }}>Sick (SL)</div>
                    <div style={{ fontSize: "15px", fontWeight: "800", color: "#0f766e" }}>{data.total_sl ?? 0}d</div>
                  </div>
                  <div style={{ padding: "6px", borderRadius: "8px", background: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.2)" }}>
                    <div style={{ fontSize: "10px", color: "#b45309", fontWeight: "700" }}>Off Days (Comp-Off)</div>
                    <div style={{ fontSize: "15px", fontWeight: "800", color: "#b45309" }}>{(data.total_off_days ?? data.total_compoff ?? 0)}d</div>
                  </div>
                </div>

                {/* Ledger Table */}
                <div style={{ overflowX: "auto", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11.5px" }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", color: "#475569", borderBottom: "1px solid #e2e8f0", textAlign: "left" }}>
                        <th style={{ padding: "7px 10px" }}>Employee</th>
                        <th style={{ padding: "7px 10px" }}>Department</th>
                        <th style={{ padding: "7px 8px", textAlign: "center" }}>CL</th>
                        <th style={{ padding: "7px 8px", textAlign: "center" }}>SL</th>
                        <th style={{ padding: "7px 8px", textAlign: "center" }}>Off Days</th>
                        <th style={{ padding: "7px 10px", textAlign: "right" }}>Total Available</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedRecords.length === 0 ? (
                        <tr>
                          <td colSpan="6" style={{ textAlign: "center", padding: "14px", color: "#64748b" }}>
                            No employees found matching the search criteria.
                          </td>
                        </tr>
                      ) : (
                        pagedRecords.map((r, idx) => (
                          <tr
                            key={r.emp_code || idx}
                            style={{
                              borderBottom: "1px solid #f1f5f9",
                              background: idx % 2 === 0 ? "#ffffff" : "#fbfcfe",
                            }}
                          >
                            <td style={{ padding: "7px 10px" }}>
                              <div style={{ fontWeight: "700", color: "#0f172a" }}>{r.emp_name}</div>
                              <div style={{ fontSize: "10.5px", color: "#64748b" }}>{r.emp_code} • {r.designation || "-"}</div>
                            </td>
                            <td style={{ padding: "7px 10px", color: "#334155" }}>
                              {r.department || "-"}
                            </td>
                            <td style={{ padding: "7px 8px", textAlign: "center", fontWeight: "600", color: "#1d4ed8" }}>
                              {r.casual_leave ?? 0}
                            </td>
                            <td style={{ padding: "7px 8px", textAlign: "center", fontWeight: "600", color: "#0f766e" }}>
                              {r.sick_leave ?? 0}
                            </td>
                            <td style={{ padding: "7px 8px", textAlign: "center", fontWeight: "600", color: "#b45309" }}>
                              {r.off_days ?? r.compoff ?? 0}
                            </td>
                            <td style={{ padding: "7px 10px", textAlign: "right" }}>
                              <span
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: "10px",
                                  fontWeight: "700",
                                  fontSize: "11px",
                                  background: (r.total_available ?? 0) > 0 ? "rgba(16, 185, 129, 0.15)" : "#f1f5f9",
                                  color: (r.total_available ?? 0) > 0 ? "#059669" : "#64748b",
                                }}
                              >
                                {r.total_available ?? 0} days
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "10px",
                      paddingTop: "8px",
                      borderTop: "1px solid #f1f5f9",
                      fontSize: "11px",
                      color: "#64748b",
                    }}
                  >
                    <span>
                      Showing {(safePage - 1) * pageSize + 1} - {Math.min(safePage * pageSize, filteredRecords.length)} of {filteredRecords.length}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <button
                        disabled={safePage <= 1}
                        onClick={() => setLeaveBalPage((p) => Math.max(1, p - 1))}
                        style={{
                          border: "1px solid #cbd5e1",
                          background: safePage <= 1 ? "#f8fafc" : "#ffffff",
                          color: safePage <= 1 ? "#94a3b8" : "#0e497b",
                          borderRadius: "6px",
                          padding: "2px 6px",
                          cursor: safePage <= 1 ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <ChevronLeft size={12} /> Prev
                      </button>
                      <span style={{ fontWeight: "700", color: "#0e497b" }}>
                        {safePage} / {totalPages}
                      </span>
                      <button
                        disabled={safePage >= totalPages}
                        onClick={() => setLeaveBalPage((p) => Math.min(totalPages, p + 1))}
                        style={{
                          border: "1px solid #cbd5e1",
                          background: safePage >= totalPages ? "#f8fafc" : "#ffffff",
                          color: safePage >= totalPages ? "#94a3b8" : "#0e497b",
                          borderRadius: "6px",
                          padding: "2px 6px",
                          cursor: safePage >= totalPages ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Next <ChevronRight size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ══════════════════════════════════════════════════ */}
          {/* 4. WIDGET: Leave Balances Metric Cards (CL, SL, Off Days only) */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "metric_cards" && data && (
            <div style={{ marginTop: "12px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                <div
                  style={{
                    padding: "10px",
                    borderRadius: "10px",
                    background: "rgba(219, 234, 254, 0.6)",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "11px", color: "#1e40af", fontWeight: "600" }}>Casual Leave (CL)</div>
                  <div style={{ fontSize: "20px", fontWeight: "800", color: "#1d4ed8", margin: "2px 0" }}>{data.casual_leave ?? 0}</div>
                  <div style={{ fontSize: "10px", color: "#475569" }}>
                    <span>Allotment: <strong>{data.casual_leave_total ?? 0}</strong></span>
                    <span style={{ margin: "0 4px" }}>•</span>
                    <span>Taken: <strong>{data.casual_leave_used ?? 0}</strong></span>
                  </div>
                </div>
                <div
                  style={{
                    padding: "10px",
                    borderRadius: "10px",
                    background: "rgba(204, 251, 241, 0.6)",
                    border: "1px solid rgba(20, 184, 166, 0.3)",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "11px", color: "#115e59", fontWeight: "600" }}>Sick Leave (SL)</div>
                  <div style={{ fontSize: "20px", fontWeight: "800", color: "#0f766e", margin: "2px 0" }}>{data.sick_leave ?? 0}</div>
                  <div style={{ fontSize: "10px", color: "#475569" }}>
                    <span>Allotment: <strong>{data.sick_leave_total ?? 0}</strong></span>
                    <span style={{ margin: "0 4px" }}>•</span>
                    <span>Taken: <strong>{data.sick_leave_used ?? 0}</strong></span>
                  </div>
                </div>
                <div
                  style={{
                    padding: "10px",
                    borderRadius: "10px",
                    background: "rgba(254, 243, 199, 0.6)",
                    border: "1px solid rgba(245, 158, 11, 0.3)",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "11px", color: "#b45309", fontWeight: "600" }}>Off Days (Comp-Off)</div>
                  <div style={{ fontSize: "20px", fontWeight: "800", color: "#d97706", margin: "2px 0" }}>{data.off_days ?? data.compoff ?? 0}</div>
                  <div style={{ fontSize: "10px", color: "#475569" }}>
                    <span>Available Credit</span>
                  </div>
                </div>
              </div>

              {/* Exact Leave Ledger Table matching portal image */}
              <div
                style={{
                  marginTop: "10px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #cbd5e1",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", textAlign: "center" }}>
                  <thead>
                    <tr style={{ background: "#1e5b8c", color: "#ffffff" }}>
                      <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: "700" }}>Category</th>
                      <th style={{ padding: "8px 10px", fontWeight: "700" }}>SL</th>
                      <th style={{ padding: "8px 10px", fontWeight: "700" }}>CL</th>
                      <th style={{ padding: "8px 10px", fontWeight: "700" }}>Off Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: "#2a6f97", color: "#ffffff", borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
                      <td style={{ padding: "7px 10px", textAlign: "left", fontWeight: "700" }}>Total (Taken)</td>
                      <td style={{ padding: "7px 10px" }}>{data.sick_leave_used ?? 0}</td>
                      <td style={{ padding: "7px 10px" }}>{data.casual_leave_used ?? 0}</td>
                      <td style={{ padding: "7px 10px" }}>0</td>
                    </tr>
                    <tr style={{ background: "#ffffff", color: "#0f172a", borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "7px 10px", textAlign: "left", fontWeight: "700", color: "#1e293b" }}>Allotment</td>
                      <td style={{ padding: "7px 10px", fontWeight: "600" }}>{data.sick_leave_total ?? 0}</td>
                      <td style={{ padding: "7px 10px", fontWeight: "600" }}>{data.casual_leave_total ?? 0}</td>
                      <td style={{ padding: "7px 10px", color: "#94a3b8" }}>-</td>
                    </tr>
                    <tr style={{ background: "#2a6f97", color: "#ffffff" }}>
                      <td style={{ padding: "7px 10px", textAlign: "left", fontWeight: "700" }}>Balance</td>
                      <td style={{ padding: "7px 10px", fontWeight: "800", fontSize: "13px" }}>{data.sick_leave ?? 0}</td>
                      <td style={{ padding: "7px 10px", fontWeight: "800", fontSize: "13px" }}>{data.casual_leave ?? 0}</td>
                      <td style={{ padding: "7px 10px", fontWeight: "800", fontSize: "13px" }}>{data.off_days ?? data.compoff ?? 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Total Available Balance Banner */}
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(5, 150, 105, 0.08))",
                  border: "1px solid rgba(16, 185, 129, 0.28)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <CheckCircle2 size={15} color="#059669" />
                  <span style={{ fontSize: "12px", fontWeight: "600", color: "#065f46" }}>
                    Total Available Leave Balance (CL + SL + Off Days)
                  </span>
                </div>
                <span style={{ fontSize: "14px", fontWeight: "800", color: "#059669" }}>
                  {data.total_available ?? ((data.casual_leave || 0) + (data.sick_leave || 0) + (data.off_days || data.compoff || 0))} Days
                </span>
              </div>
            </div>
          )}


          {/* ══════════════════════════════════════════════════ */}
          {/* 5. WIDGET: Payslip Card                             */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "payslip_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "14px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
                boxShadow: "0 4px 14px rgba(14, 73, 123, 0.08)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontWeight: "700", color: "#0e497b" }}>Payslip {data.period}</span>
                <span style={{ fontSize: "11px", color: "#64748b" }}>Payable Days: {data.payable_days}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px dashed rgba(14, 73, 123, 0.2)",
                }}
              >
                <span style={{ color: "#475569" }}>Gross Earnings:</span>
                <span style={{ fontWeight: "700", color: "#0f172a" }}>₹{Number(data.gross_salary || 0).toLocaleString()}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px dashed rgba(14, 73, 123, 0.2)",
                }}
              >
                <span style={{ color: "#dc2626" }}>Total Deductions:</span>
                <span style={{ fontWeight: "700", color: "#dc2626" }}>
                  -₹{Number(data.total_deductions || 0).toLocaleString()}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 0 4px 0",
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#059669",
                }}
              >
                <span>Net Take-Home:</span>
                <span>₹{Number(data.net_salary || 0).toLocaleString()}</span>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 6. WIDGET: Holiday List                             */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "holiday_timeline" && data?.holidays && (
            <div
              style={{
                marginTop: "12px",
                maxHeight: "220px",
                overflowY: "auto",
                padding: "10px",
                borderRadius: "12px",
                background: "rgba(240, 247, 255, 0.8)",
                border: "1px solid rgba(14, 73, 123, 0.18)",
              }}
            >
              {data.holidays.map((h, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: "1px solid rgba(14, 73, 123, 0.12)",
                    fontSize: "12px",
                  }}
                >
                  <span style={{ fontWeight: "600", color: "#0f172a" }}>{h.holiday_name}</span>
                  <span style={{ color: "#0e497b", fontWeight: "600" }}>
                    {h.holiday_date} ({h.day})
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 7. WIDGET: Upcoming Celebrations                    */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "celebrations_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "14px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(244, 114, 182, 0.35)",
              }}
            >
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#db2777", marginBottom: "10px" }}>
                Celebrations in {data.month}
              </div>
              {data.birthdays && data.birthdays.length > 0 && (
                <div style={{ marginBottom: "10px" }}>
                  <div style={{ fontSize: "11px", color: "#0f172a", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                    <Cake size={13} color="#db2777" /> Birthdays:
                  </div>
                  {data.birthdays.map((b, i) => (
                    <div key={i} style={{ fontSize: "12px", color: "#475569", padding: "3px 0 3px 14px" }}>
                      • {b.first_name} {b.last_name} ({b.dob}) - {b.department__name || "-"}
                    </div>
                  ))}
                </div>
              )}
              {data.anniversaries && data.anniversaries.length > 0 && (
                <div>
                  <div style={{ fontSize: "11px", color: "#0f172a", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                    <Gift size={13} color="#2563eb" /> Work Anniversaries:
                  </div>
                  {data.anniversaries.map((a, i) => (
                    <div key={i} style={{ fontSize: "12px", color: "#475569", padding: "3px 0 3px 14px" }}>
                      • {a.first_name} {a.last_name} ({a.date_of_joining}) - {a.department__name || "-"}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 8. WIDGET: Employee Profile Card                    */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "profile_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
                boxShadow: "0 4px 14px rgba(14, 73, 123, 0.08)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #0e497b, #1d4ed8)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "700",
                    fontSize: "16px",
                  }}
                >
                  {(data.name || "E").substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: "15px", fontWeight: "700", color: "#0f172a" }}>{data.name}</div>
                  <div style={{ fontSize: "12px", color: "#0e497b", fontWeight: "600" }}>{data.emp_code} • {data.designation}</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "12px" }}>
                <div style={{ color: "#64748b" }}>Department: <strong style={{ color: "#0f172a" }}>{data.department}</strong></div>
                <div style={{ color: "#64748b" }}>Location: <strong style={{ color: "#0f172a" }}>{data.work_location}</strong></div>
                <div style={{ color: "#64748b" }}>Manager: <strong style={{ color: "#0f172a" }}>{data.reporting_manager}</strong></div>
                <div style={{ color: "#64748b" }}>Status: <strong style={{ color: "#059669" }}>{data.status}</strong></div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 9. WIDGET: Helpdesk Tickets Table                   */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "table_tickets" && data?.tickets && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px",
                borderRadius: "14px",
                background: "rgba(240, 247, 255, 0.85)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                  paddingBottom: "6px",
                  borderBottom: "1px solid rgba(14, 73, 123, 0.15)",
                }}
              >
                <div style={{ fontSize: "12.5px", fontWeight: "700", color: "#0e497b" }}>
                  Helpdesk Support Tickets ({data.tickets.length})
                </div>
                <button
                  onClick={() => handleDownloadTicketsExcel(data.tickets)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "3px 9px",
                    fontSize: "11px",
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                  title="Download tickets as Excel (.xlsx)"
                >
                  <FileSpreadsheet size={12} />
                  <span>Excel</span>
                  <Download size={10} />
                </button>
              </div>

              {data.tickets.map((t, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: "1px solid rgba(14, 73, 123, 0.12)",
                    fontSize: "12px",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: "700", color: "#0e497b", marginRight: "8px" }}>#{t.ticket_number}</span>
                    <span style={{ color: "#0f172a" }}>{t.subject}</span>
                  </div>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <span
                      style={{
                        fontSize: "10px",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        background: "rgba(245, 158, 11, 0.15)",
                        color: "#d97706",
                        fontWeight: "600",
                      }}
                    >
                      {t.priority}
                    </span>
                    <span
                      style={{
                        fontSize: "10px",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        background: "rgba(14, 73, 123, 0.15)",
                        color: "#0e497b",
                        fontWeight: "600",
                      }}
                    >
                      {t.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 10. WIDGET: Leave Requests Table                    */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "table_leaves" && data?.leaves && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px",
                borderRadius: "14px",
                background: "rgba(240, 247, 255, 0.85)",
                border: "1px solid rgba(14, 73, 123, 0.2)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                  paddingBottom: "6px",
                  borderBottom: "1px solid rgba(14, 73, 123, 0.15)",
                }}
              >
                <div style={{ fontSize: "12.5px", fontWeight: "700", color: "#0e497b" }}>
                  Team Leave Applications ({data.leaves.length})
                </div>
                <button
                  onClick={() => handleDownloadLeavesExcel(data.leaves)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "3px 9px",
                    fontSize: "11px",
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                  title="Download leave requests as Excel (.xlsx)"
                >
                  <FileSpreadsheet size={12} />
                  <span>Excel</span>
                  <Download size={10} />
                </button>
              </div>

              {data.leaves.map((l, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: "1px solid rgba(14, 73, 123, 0.12)",
                    fontSize: "12px",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: "700", color: "#0e497b", marginRight: "6px" }}>{l.leave_type}</span>
                    <span style={{ color: "#64748b" }}>{l.from_date} to {l.to_date} ({l.total_days}d)</span>
                  </div>
                  <span
                    style={{
                      fontSize: "11px",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      fontWeight: "700",
                      background:
                        l.leave_status === "Approved"
                          ? "rgba(16, 185, 129, 0.15)"
                          : l.leave_status === "Rejected"
                            ? "rgba(239, 68, 68, 0.15)"
                            : "rgba(245, 158, 11, 0.15)",
                      color:
                        l.leave_status === "Approved"
                          ? "#059669"
                          : l.leave_status === "Rejected"
                            ? "#dc2626"
                            : "#d97706",
                    }}
                  >
                    {l.leave_status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 11. WIDGET: Leave Application Success Card          */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "leave_application_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.94)",
                border: "1.5px solid rgba(16, 185, 129, 0.45)",
                boxShadow: "0 6px 20px rgba(16, 185, 129, 0.1)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CheckCircle2 size={18} color="#059669" />
                  <span style={{ fontWeight: "700", color: "#0f172a", fontSize: "14px" }}>
                    {data.leave_type} Leave Application
                  </span>
                  <span style={{ fontSize: "11px", color: "#065f46", background: "rgba(16, 185, 129, 0.15)", padding: "2px 8px", borderRadius: "10px", fontWeight: "700" }}>
                    #{data.leave_id}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    background: data.status === "Approved" ? "rgba(16, 185, 129, 0.2)" : "rgba(245, 158, 11, 0.2)",
                    color: data.status === "Approved" ? "#059669" : "#d97706",
                  }}
                >
                  {data.status}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "12px", marginBottom: "12px" }}>
                <div style={{ color: "#64748b" }}>Employee: <strong style={{ color: "#0f172a" }}>{data.emp_name} ({data.emp_code})</strong></div>
                <div style={{ color: "#64748b" }}>Duration: <strong style={{ color: "#0f172a" }}>{data.duration_type} {data.session && data.session !== "-" ? `(${data.session})` : ""}</strong></div>
                <div style={{ color: "#64748b" }}>Start Date: <strong style={{ color: "#0e497b" }}>{data.start_date}</strong></div>
                <div style={{ color: "#64748b" }}>End Date: <strong style={{ color: "#0e497b" }}>{data.end_date}</strong></div>
                <div style={{ color: "#64748b" }}>Working Days: <strong style={{ color: "#059669" }}>{data.days_count} day(s)</strong></div>
                <div style={{ color: "#64748b" }}>Reason: <strong style={{ color: "#0f172a" }}>"{data.reason}"</strong></div>
              </div>

              {data.approvers && data.approvers.length > 0 && (
                <div style={{ paddingTop: "8px", borderTop: "1px dashed rgba(14, 73, 123, 0.2)", fontSize: "11px", color: "#64748b" }}>
                  <span style={{ fontWeight: "700", color: "#0e497b" }}>Approval Chain: </span>
                  {data.approvers.join(" ➔ ")}
                </div>
              )}
              {data.auto_approved && (
                <div style={{ paddingTop: "8px", borderTop: "1px dashed rgba(16, 185, 129, 0.35)", fontSize: "11px", color: "#059669", fontWeight: "600" }}>
                  ⚡ Auto-approved via HOD / HR Management authority.
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 12. WIDGET: Permission Application Success Card     */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "permission_application_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.94)",
                border: "1.5px solid rgba(14, 73, 123, 0.35)",
                boxShadow: "0 6px 20px rgba(14, 73, 123, 0.08)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Clock size={18} color="#0e497b" />
                  <span style={{ fontWeight: "700", color: "#0f172a", fontSize: "14px" }}>
                    Permission Request
                  </span>
                  <span style={{ fontSize: "11px", color: "#0e497b", background: "rgba(14, 73, 123, 0.12)", padding: "2px 8px", borderRadius: "10px", fontWeight: "700" }}>
                    #{data.permission_id}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    background: data.status === "Approved" ? "rgba(16, 185, 129, 0.2)" : "rgba(245, 158, 11, 0.2)",
                    color: data.status === "Approved" ? "#059669" : "#d97706",
                  }}
                >
                  {data.status}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "12px", marginBottom: "12px" }}>
                <div style={{ color: "#64748b" }}>Employee: <strong style={{ color: "#0f172a" }}>{data.emp_name} ({data.emp_code})</strong></div>
                <div style={{ color: "#64748b" }}>Date: <strong style={{ color: "#0e497b" }}>{data.date}</strong></div>
                <div style={{ color: "#64748b" }}>Scheduled Time: <strong style={{ color: "#0f172a" }}>{data.time}</strong></div>
                <div style={{ color: "#64748b" }}>Duration: <strong style={{ color: "#0e497b" }}>{data.hours_requested} hr(s) ({data.session})</strong></div>
                <div style={{ color: "#64748b", gridColumn: "span 2" }}>Reason: <strong style={{ color: "#0f172a" }}>"{data.reason}"</strong></div>
              </div>

              {data.approvers && data.approvers.length > 0 && (
                <div style={{ paddingTop: "8px", borderTop: "1px dashed rgba(14, 73, 123, 0.2)", fontSize: "11px", color: "#64748b" }}>
                  <span style={{ fontWeight: "700", color: "#0e497b" }}>Approval Chain: </span>
                  {data.approvers.join(" ➔ ")}
                </div>
              )}
              {data.auto_approved && (
                <div style={{ paddingTop: "8px", borderTop: "1px dashed rgba(16, 185, 129, 0.35)", fontSize: "11px", color: "#059669", fontWeight: "600" }}>
                  ⚡ Auto-approved via HOD / HR Management authority.
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 13. WIDGET: Comp-Off Summary Card                   */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "compoff_summary_card" && data && (
            <div
              style={{
                marginTop: "12px",
                padding: "16px",
                borderRadius: "14px",
                background: "rgba(255, 255, 255, 0.94)",
                border: "1px solid rgba(14, 73, 123, 0.22)",
                boxShadow: "0 4px 16px rgba(14, 73, 123, 0.08)",
              }}
            >
              {/* Header & Excel Download */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Award size={18} color="#0e497b" />
                  <span style={{ fontWeight: "700", color: "#0e497b", fontSize: "13.5px" }}>
                    Comp-Off Summary • {data.month_name} {data.year}
                  </span>
                  {data.emp_code && (
                    <span
                      style={{
                        fontSize: "11px",
                        background: "rgba(14, 73, 123, 0.1)",
                        color: "#0e497b",
                        padding: "2px 7px",
                        borderRadius: "6px",
                        fontWeight: "600",
                      }}
                    >
                      {data.emp_code}
                    </span>
                  )}
                </div>

                <button
                  onClick={() => handleDownloadCompoffExcel(data)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    padding: "5px 11px",
                    fontSize: "11.5px",
                    fontWeight: "700",
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
                    transition: "all 0.15s ease",
                  }}
                  title="Download Comp-Off Statement as Excel (.xlsx)"
                >
                  <FileSpreadsheet size={13} />
                  <span>Download Excel (.xlsx)</span>
                  <Download size={11} />
                </button>
              </div>

              {/* 4 KPI Metric Cards */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    padding: "8px 10px",
                    borderRadius: "10px",
                    background: "rgba(240, 247, 255, 0.9)",
                    border: "1px solid rgba(14, 73, 123, 0.2)",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "10.5px", color: "#64748b", fontWeight: "600" }}>Taken ({data.month_name?.slice(0, 3)})</div>
                  <div style={{ fontSize: "16px", fontWeight: "800", color: "#0e497b", marginTop: "2px" }}>
                    {data.taken_days ?? 0}
                  </div>
                  <div style={{ fontSize: "10px", color: "#0e497b" }}>Days Taken</div>
                </div>

                <div
                  style={{
                    padding: "8px 10px",
                    borderRadius: "10px",
                    background: "rgba(16, 185, 129, 0.08)",
                    border: "1px solid rgba(16, 185, 129, 0.25)",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "10.5px", color: "#065f46", fontWeight: "600" }}>Available Balance</div>
                  <div style={{ fontSize: "16px", fontWeight: "800", color: "#059669", marginTop: "2px" }}>
                    {data.available_balance ?? 0}
                  </div>
                  <div style={{ fontSize: "10px", color: "#059669" }}>Valid Days</div>
                </div>

                <div
                  style={{
                    padding: "8px 10px",
                    borderRadius: "10px",
                    background: "rgba(245, 158, 11, 0.08)",
                    border: "1px solid rgba(245, 158, 11, 0.25)",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "10.5px", color: "#92400e", fontWeight: "600" }}>Pending Requests</div>
                  <div style={{ fontSize: "16px", fontWeight: "800", color: "#d97706", marginTop: "2px" }}>
                    {data.pending_days ?? 0}
                  </div>
                  <div style={{ fontSize: "10px", color: "#d97706" }}>Days Awaiting</div>
                </div>

                <div
                  style={{
                    padding: "8px 10px",
                    borderRadius: "10px",
                    background: "rgba(99, 102, 241, 0.08)",
                    border: "1px solid rgba(99, 102, 241, 0.25)",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "10.5px", color: "#3730a3", fontWeight: "600" }}>Earned ({data.month_name?.slice(0, 3)})</div>
                  <div style={{ fontSize: "16px", fontWeight: "800", color: "#4f46e5", marginTop: "2px" }}>
                    {data.credits_earned_this_month ?? 0}
                  </div>
                  <div style={{ fontSize: "10px", color: "#4f46e5" }}>Credits Generated</div>
                </div>
              </div>

              {/* Taken Records Table (if any) */}
              {data.taken_records && data.taken_records.length > 0 && (
                <div style={{ marginTop: "10px" }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#0e497b", marginBottom: "6px" }}>
                    Leaves Taken in {data.month_name} ({data.taken_records.length}):
                  </div>
                  <div style={{ borderRadius: "8px", overflow: "hidden", border: "1px solid rgba(14, 73, 123, 0.15)" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11.5px" }}>
                      <thead>
                        <tr style={{ background: "rgba(14, 73, 123, 0.08)", color: "#0e497b", textAlign: "left" }}>
                          <th style={{ padding: "6px 10px" }}>Date</th>
                          <th style={{ padding: "6px 10px" }}>Days</th>
                          <th style={{ padding: "6px 10px" }}>Duration</th>
                          <th style={{ padding: "6px 10px" }}>Status</th>
                          <th style={{ padding: "6px 10px" }}>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.taken_records.map((r, i) => (
                          <tr key={i} style={{ borderTop: "1px solid rgba(14, 73, 123, 0.1)" }}>
                            <td style={{ padding: "6px 10px", fontWeight: "600", color: "#0f172a" }}>{r.date}</td>
                            <td style={{ padding: "6px 10px", color: "#0e497b" }}>{r.days}</td>
                            <td style={{ padding: "6px 10px", color: "#475569" }}>{r.duration_type}</td>
                            <td style={{ padding: "6px 10px" }}>
                              <span style={{ fontSize: "10.5px", padding: "2px 7px", borderRadius: "10px", fontWeight: "700", background: "rgba(16, 185, 129, 0.15)", color: "#059669" }}>
                                {r.status}
                              </span>
                            </td>
                            <td style={{ padding: "6px 10px", color: "#64748b", fontStyle: "italic" }}>{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Active Credit Batches (if any) */}
              {data.valid_credits && data.valid_credits.length > 0 && (
                <div style={{ marginTop: "10px", paddingTop: "8px", borderTop: "1px dashed rgba(14, 73, 123, 0.18)" }}>
                  <div style={{ fontSize: "11.5px", fontWeight: "700", color: "#0e497b", marginBottom: "4px" }}>
                    Active Credit Batches (90-Day Expiry):
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {data.valid_credits.map((c, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: "11px",
                          padding: "4px 8px",
                          borderRadius: "8px",
                          background: "rgba(240, 247, 255, 0.9)",
                          border: "1px solid rgba(14, 73, 123, 0.18)",
                          color: "#1e293b",
                        }}
                      >
                        <strong>{c.work_date}</strong>: {c.days_available} day ({c.status}) • <span style={{ color: "#d97706" }}>Exp: {c.expiry_date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* 14. WIDGET: Error / Policy Restriction Banner       */}
          {/* ══════════════════════════════════════════════════ */}
          {displayType === "error" && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px 14px",
                borderRadius: "12px",
                background: "rgba(254, 242, 242, 0.9)",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <Shield size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: "2px" }} />
              <div style={{ fontSize: "12px", color: "#991b1b", lineHeight: "1.5" }}>
                <strong>Policy Restriction or Validation Notice:</strong> Please review your permissions or submitted dates.
              </div>
            </div>
          )}

          {/* Sources / RAG Citations */}
          {message.sources && message.sources.length > 0 && (
            <div
              style={{
                marginTop: "12px",
                borderTop: "1px solid rgba(14, 73, 123, 0.15)",
                paddingTop: "8px",
                fontSize: "11px",
                color: "#64748b",
              }}
            >
              <strong>Referenced Policy Knowledge:</strong>
              <ul style={{ paddingLeft: "16px", margin: "4px 0 0 0" }}>
                {message.sources.map((s, idx) => (
                  <li key={idx}>
                    📄 {s.filename} {s.page ? `(Page ${s.page})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ── CARD FOOTER (TIMESTAMP & ACTION BUTTONS) ── */}
        <div
          style={{
            display: "flex",
            justifyContent: isUser ? "flex-end" : "space-between",
            alignItems: "center",
            marginTop: "6px",
            padding: "0 6px",
          }}
        >
          {/* Timestamp */}
          {message.time && (
            <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
              {message.time}
            </span>
          )}

          {/* Quick Action Icons for AI message: Copy, Thumbs Up, Thumbs Down */}
          {!isUser && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* Copy */}
              <button
                onClick={handleCopy}
                title={copied ? "Copied!" : "Copy message"}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: copied ? "#059669" : "#64748b",
                  display: "flex",
                  alignItems: "center",
                  padding: "3px",
                  borderRadius: "4px",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#0e497b")}
                onMouseLeave={(e) => (e.currentTarget.style.color = copied ? "#059669" : "#64748b")}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>

              {/* Thumbs Up */}
              <button
                onClick={() => setFeedback(feedback === "like" ? null : "like")}
                title="Good response"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: feedback === "like" ? "#0e497b" : "#64748b",
                  display: "flex",
                  alignItems: "center",
                  padding: "3px",
                  borderRadius: "4px",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (feedback !== "like") e.currentTarget.style.color = "#0e497b";
                }}
                onMouseLeave={(e) => {
                  if (feedback !== "like") e.currentTarget.style.color = "#64748b";
                }}
              >
                <ThumbsUp size={14} fill={feedback === "like" ? "#0e497b" : "none"} />
              </button>

              {/* Thumbs Down */}
              <button
                onClick={() => setFeedback(feedback === "dislike" ? null : "dislike")}
                title="Poor response"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: feedback === "dislike" ? "#dc2626" : "#64748b",
                  display: "flex",
                  alignItems: "center",
                  padding: "3px",
                  borderRadius: "4px",
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (feedback !== "dislike") e.currentTarget.style.color = "#dc2626";
                }}
                onMouseLeave={(e) => {
                  if (feedback !== "dislike") e.currentTarget.style.color = "#64748b";
                }}
              >
                <ThumbsDown size={14} fill={feedback === "dislike" ? "#dc2626" : "none"} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── USER AVATAR ON THE RIGHT (BLUE THEME) ── */}
      {isUser && (
        <div
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #0e497b, #1d4ed8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            flexShrink: 0,
            boxShadow: "0 4px 10px rgba(14, 73, 123, 0.28)",
            marginTop: "2px",
          }}
        >
          <User size={18} />
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
